import { createMapLayerPainter } from "../components/mapLayerCanvas";
import {
  mapPage,
  pointOnMap,
  drawMapFrame,
  croppedMapPage,
  clampPointToMapPage,
} from "../lib/mapPage";
import { transformLabel } from "../lib/mapLabelTransform";
import {
  LANDMARK_HANDLES,
  rotatePoint,
  landmarkHandleCursor,
  hitLandmarkHandle,
  landmarkHandles,
  landmarkLocalPoint,
  landmarkCorners,
  hitTestLandmarkMark,
  landmarkNativeSize,
  transformLandmark,
  type LandmarkHandle,
} from "../lib/mapLandmarkTransform";
import {
  hitMapImageHandle,
  hitTestMapImage,
  mapImageCorners,
  mapImageDrawSize,
  mapImageHandles,
  mapImageLocalPoint,
  transformMapImage,
} from "../lib/mapImageTransform";
import { readProjectAssetDataUrl } from "../lib/tauri/assets";
import { drawMapPaperGrain } from "../lib/mapPaperGrain";
import { assertNever } from "../lib/assertNever";
import { openContextMenu } from "../lib/platform/contextMenu";
import { useTea } from "../lib/tea";
import {
  emptyRiverDraft,
  initialMapToolsModel,
  riverDraftHasPoints,
  updateMapTools,
  type MapToolsCmd,
  type MapTool,
  type Selection,
  type WaterMode,
} from "./useMapTools.update";
export type { MapTool, Selection, WaterMode } from "./useMapTools.update";
import {
  computed,
  watch,
  type Ref,
  type ComputedRef,
  type CSSProperties,
  type WritableComputedRef,
} from "vue";
import {
  type Maybe,
  andThenMaybe,
  atIndexMaybe,
  toMaybe,
  nothing,
  just,
  isJust,
  isNothing,
} from "../lib/maybe";
import {
  activeMapLayer,
  MAP_ZOOM_MIN,
  MAP_ZOOM_MAX,
  clampMapObjectSize,
  LANDMARK_SIZE_MIN,
  mapObjectSizeMin,
  clampLabelSize,
  labelFontPx,
  labelScale,
  landmarkInk,
  landmarkScale,
  labelInk,
  mapImagePlaceSize,
  mapImageScale,
  isLabelsLocked,
  isLabelsVisible,
  isPaperLocked,
  isPaperVisible,
  mapPaperColor,
  storedMapInk,
  storedMapPaper,
  newMapId,
  parseMapDocument,
  pruneOrphanNodes,
  serializeMapDocument,
  type MapDocument,
  type MapImage,
  type MapLandmark,
  type MapLabel,
  type MapLabelFont,
  type MapLandmass,
  type MapLayer,
  type MapPath,
  type MapPathWeight,
  type MapPathStyle,
  type MapFill,
} from "../lib/mapDocument";
import {
  distance,
  distanceToSegment,
  drawGrid,
  drawLandmarkMark,
  landmarkDrawSize,
  findNodeNear,
  hitTestNode,
  hitTestPath,
  joinThresholdWorld,
  nodeById,
  pathLineWidth,
  labelCanvasFont,
  drawMapLabel,
  measureMapLabel,
  pathLineDash,
  pathPoints,
  screenToWorld,
  traceSmoothPath,
  worldToScreen,
  type ScreenPoint,
  type WorldPoint,
} from "../lib/mapGeometry";
import {
  cycleKey,
  drawMapFill,
  fillPoints,
  fillsBackToFront,
  findFillLoop,
  MAP_FILL_HOVER_ALPHA,
  pointInPolygon,
  signedPolygonArea,
} from "../lib/mapFill";
import {
  buildStoryPath,
  findLandmarkPlace,
  findMarkerPlace,
  findStoryMarkerAt,
  storyPlaceHover,
  type StoryArcMomentRef,
  type StoryPlaceHover,
  type StoryPlaceRef,
} from "../lib/mapStoryPath";
import {
  drawStoryPath,
  storyPathPlaceKey,
  storyPathStopHitRadiusWorld,
} from "../lib/mapStoryPathDraw";
import {
  applyCanvasImageData,
  clampLandmassBrushSize,
  clampLandmassCoastRoughness,
  cloneCanvasImageData,
  createDefaultLandmass,
  isLandmassLocked,
  isLandmassVisible,
  createEmptyLandmassCanvas,
  cssColorToRgb,
  drawLandmassMask,
  drawLandmassPreview,
  type TerrainBrushStyle,
  extractCoastPolylines,
  findRiverForkAttach,
  floodFillLandColor,
  landmassCoastRoughness01,
  stampLandmassBrush,
  stampRiverCorridor,
  traceJaggedBrushPath,
  worldToMaskPixel,
  type CoastPolyline,
  type LandmassBrushMode,
} from "../lib/mapLandmass";

/** Values and actions needed by MapEditor.vue. Transient gesture details stay
 * private to the tool loop. Absence stays Maybe through the canvas adapter. */
export type MapTools = {
  tool: ComputedRef<MapTool>;
  selection: WritableComputedRef<Maybe<Selection>>;
  inkColor: WritableComputedRef<string>;
  fillColor: WritableComputedRef<string>;
  inkWeight: WritableComputedRef<MapPathWeight>;
  inkStyle: WritableComputedRef<MapPathStyle>;
  panning: ComputedRef<boolean>;
  spaceHeld: ComputedRef<boolean>;
  landmarkStamp: WritableComputedRef<Maybe<string>>;
  imageStamp: WritableComputedRef<Maybe<string>>;
  labelSize: WritableComputedRef<number>;
  labelFont: WritableComputedRef<MapLabelFont>;
  labelRotation: WritableComputedRef<number>;
  labelCurve: WritableComputedRef<number>;
  labelSpacing: WritableComputedRef<number>;
  applyLabelAppearance: (recordUndo: boolean) => void;
  labelDraft: WritableComputedRef<string>;
  labelDialogOpen: ComputedRef<boolean>;
  labelEditorStyle: ComputedRef<CSSProperties>;
  editingLabel: ComputedRef<Maybe<{ id: string }>>;
  shortcutsOpen: ComputedRef<boolean>;
  inkTargetSelected: ComputedRef<boolean>;
  fillTargetSelected: ComputedRef<boolean>;
  sizeSliderKind: ComputedRef<Maybe<"label" | "landmark" | "image">>;
  /** Size slider floor so the mark can shrink to 1 world px. */
  objectSizeMin: ComputedRef<number>;
  showResizeCursor: ComputedRef<boolean>;
  resizeCursor: ComputedRef<string>;
  sizeSliderModel: WritableComputedRef<number>;
  landmassBrushSize: WritableComputedRef<number>;
  landmassCoastRoughness: WritableComputedRef<number>;
  terrainBrushStyle: WritableComputedRef<TerrainBrushStyle>;
  terrainOpacity: WritableComputedRef<number>;
  terrainCoverage: WritableComputedRef<number>;
  waterMode: WritableComputedRef<WaterMode>;
  undoDepth: ComputedRef<number>;
  redoDepth: ComputedRef<number>;
  pushUndo: () => void;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  selectedLandmark: () => Maybe<MapLandmark>;
  selectedImage: () => Maybe<MapImage>;
  /** Select a landmark by id (any layer). False when missing. */
  selectLandmarkById: (landmarkId: string) => boolean;
  /** Loaded Assets raster for PNG export / draw. */
  getLoadedMapImage: (path: string) => Maybe<HTMLImageElement>;
  /** Hover details while the Story path tool is active. */
  storyPathHover: ComputedRef<Maybe<StoryPlaceHover>>;
  storyPathSelection: ComputedRef<Maybe<StoryPlaceHover>>;
  deleteSelection: () => void;
  cancelDraftLine: () => void;
  setTool: (next: MapTool) => void;
  scheduleDraw: () => void;
  applyInkColor: () => void;
  applyFillColor: () => void;
  applyInkWeight: () => void;
  applyInkStyle: () => void;
  splitSelectedNode: () => void;
  toggleShortcuts: () => void;
  closeShortcuts: () => void;
  cancelLabelDialog: () => void;
  commitLabel: () => void;
  /** Replace the in-memory mask from a loaded asset (or clear). */
  setLandmassFromImage: (img: Maybe<HTMLImageElement>) => void;
  /** Offscreen mask for PNG export. */
  getLandmassCanvas: () => Maybe<HTMLCanvasElement>;
  /** Shoreline polylines for PNG export (rebuilds if dirty). */
  getLandmassCoasts: () => CoastPolyline[];
  onPointerDown: (event: PointerEvent) => void;
  onPointerMove: (event: PointerEvent) => void;
  onPointerUp: (event: PointerEvent) => void;
  onPointerLeave: () => void;
  onWheel: (event: WheelEvent) => void;
  onDblClick: (event: MouseEvent) => void;
  onContextMenu: (event: MouseEvent) => void;
  onKeyDown: (event: KeyboardEvent) => void;
  onKeyUp: (event: KeyboardEvent) => void;
  layerById: (id: string) => Maybe<MapLayer>;
  cleanup: () => void;
};

/**
 * Canvas adapter: DOM events → messages → pure update → commands.
 * useMapTools.update.ts owns transient tool state and history transitions.
 * This file owns hit-testing, drawing, pointer capture, and document edits.
 * MapEditor.vue owns document persistence, layers, and export.
 *
 * Coordinates: events start in canvas-local CSS pixels, screenToWorld removes
 * pan/zoom. Landmarks, paths, and labels retain free coordinates.
 * Drawing takes the reverse route. devicePixelRatio is applied once to the
 * canvas context, not the points.
 */
export function useMapTools(options: {
  doc: Ref<MapDocument>;
  canvasEl: Readonly<Ref<Maybe<HTMLCanvasElement>>>;
  /** Project root for loading Assets/ rasters used as map images. */
  rootPath: Readonly<Ref<Maybe<string>>>;
  readonly: Ref<boolean>;
  /** Manuscript scene order for the inferred Story path (#278). */
  sceneOrder: Readonly<Ref<readonly string[]>>;
  /** Arc moments keyed by scene path for Story path hover. */
  arcMoments: Readonly<Ref<readonly StoryArcMomentRef[]>>;
  /** Called after a landmass stroke so the host can write the Assets/ PNG. */
  onLandmassDirty?: () => void;
  /**
   * Story path: dragging a path stop relocates every scene on that place
   * (landmark or free marker, one scene or many).
   */
  onStoryNodeDragStart?: (payload: {
    paths: readonly string[];
    from: StoryPlaceRef;
    event: PointerEvent;
  }) => void;
  /** Place keys to emphasize while hovering a placed row in the Story path sidebar. */
  storyHighlightKeys?: Readonly<Ref<readonly string[]>>;
}): MapTools {
  const { doc, canvasEl, rootPath, readonly, sceneOrder, arcMoments } = options;
  const mapImageEls = new Map<string, HTMLImageElement>();
  const mapImageLoadFailed = new Set<string>();
  const mapImageLoading = new Set<string>();

  function getLoadedMapImage(path: string): Maybe<HTMLImageElement> {
    const el = mapImageEls.get(path);
    return typeof el === "undefined" ? nothing : just(el);
  }

  function ensureMapImage(path: string): Maybe<HTMLImageElement> {
    const cached = getLoadedMapImage(path);
    if (isJust(cached)) return cached;
    if (mapImageLoadFailed.has(path) || mapImageLoading.has(path))
      return nothing;
    if (
      isNothing(rootPath.value) ||
      rootPath.value.value.length === 0 ||
      path.length === 0
    )
      return nothing;
    mapImageLoading.add(path);
    const root = rootPath.value.value;
    void readProjectAssetDataUrl(root, path)
      .then((dataUrl) => {
        const img = new Image();
        img.onload = () => {
          mapImageEls.set(path, img);
          mapImageLoading.delete(path);
          scheduleDraw();
        };
        img.onerror = () => {
          mapImageLoadFailed.add(path);
          mapImageLoading.delete(path);
        };
        img.src = dataUrl;
      })
      .catch(() => {
        mapImageLoadFailed.add(path);
        mapImageLoading.delete(path);
      });
    return nothing;
  }

  /** Kick off a load if needed and resolve when the raster is ready (or failed). */
  function loadMapImage(path: string): Promise<Maybe<HTMLImageElement>> {
    const cached = getLoadedMapImage(path);
    if (isJust(cached)) return Promise.resolve(cached);
    if (mapImageLoadFailed.has(path)) return Promise.resolve(nothing);
    ensureMapImage(path);
    return new Promise((resolve) => {
      const started = Date.now();
      const tick = (): void => {
        const el = getLoadedMapImage(path);
        if (isJust(el)) {
          resolve(el);
          return;
        }
        if (mapImageLoadFailed.has(path) || Date.now() - started > 8000) {
          resolve(nothing);
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  /** Parallel to doc JSON undo: mask pixels are not embedded in snapshots. */
  const maskUndoStack: Maybe<ImageData>[] = [];
  const maskRedoStack: Maybe<ImageData>[] = [];
  let landmassCanvas: Maybe<HTMLCanvasElement> = nothing;
  let lastLandmassPaint: Maybe<WorldPoint> = nothing;
  /** Shoreline polylines (mask space). Rebuilt after strokes, not every dab. */
  let cachedCoasts: CoastPolyline[] = [];
  let coastsDirty = true;
  /** Set for the active land stroke: Land tool adds, Erase tool subtracts. */
  let activeSculptMode: LandmassBrushMode = "add";
  let terrainStrokeSeed = 0;

  function invalidateCoasts(): void {
    coastsDirty = true;
  }

  function ensureCoasts(): CoastPolyline[] {
    if (coastsDirty === false) return cachedCoasts;
    if (isNothing(landmassCanvas)) {
      cachedCoasts = [];
      coastsDirty = false;
      return cachedCoasts;
    }
    cachedCoasts = extractCoastPolylines(landmassCanvas.value);
    coastsDirty = false;
    return cachedCoasts;
  }

  const { model, dispatch } = useTea(initialMapToolsModel(), updateMapTools, {
    runCmd: just((cmd: MapToolsCmd) => {
      switch (cmd.kind) {
        case "restoreDocument":
          // History restores map content; navigation stays at the current view.
          doc.value = {
            ...parseMapDocument(cmd.snapshot),
            viewport: { ...doc.value.viewport },
          };
          return;
        case "redraw":
          scheduleDraw();
          return;
        default:
          return assertNever(cmd);
      }
    }),
  });

  // Keep Maybe values intact through rendering and event handling.
  // Only toolbar v-model inputs need setters; all other transitions dispatch Msgs.
  const tool = computed(() => model.value.tool);
  function cropHandles(): { id: LandmarkHandle; point: ScreenPoint }[] {
    const crop = model.value.cropDraft;
    if (isNothing(crop)) return [];
    const a = toScreen(crop.value.start),
      b = toScreen(crop.value.end);
    const cx = (a.x + b.x) / 2,
      cy = (a.y + b.y) / 2;
    return LANDMARK_HANDLES.map((h) => ({
      id: h.id,
      point: {
        x: cx + (h.x * Math.abs(b.x - a.x)) / 2,
        y: cy + (h.y * Math.abs(b.y - a.y)) / 2,
      },
    }));
  }
  function hitCropHandle(screen: ScreenPoint): Maybe<LandmarkHandle> {
    const handle = toMaybe(
      cropHandles().find((h) => screenDist(h.point, screen) <= 8),
    );
    return isJust(handle) ? just(handle.value.id) : nothing;
  }
  function cancelCrop(): void {
    void dispatch({ type: "CropDraftChanged", value: nothing, drawing: false });
  }
  function applyCrop(): void {
    const draft = model.value.cropDraft;
    if (readonly.value || isNothing(draft)) return;
    const page = croppedMapPage(
      mapPage(doc.value),
      draft.value.start,
      draft.value.end,
    );
    if (isNothing(page)) return;
    pushUndo();
    doc.value.page = page.value;
    cancelCrop();
  }

  const selection = computed({
    get: () => model.value.selection,
    set: (value: Maybe<Selection>) => {
      void dispatch({ type: "SelectionChanged", value });
    },
  });
  const fillColor = computed({
    get: () => model.value.fillColor,
    set: (value: string) => {
      void dispatch({ type: "FillColorChanged", value });
    },
  });
  const inkColor = computed({
    get: () => model.value.inkColor,
    set: (value: string) => {
      void dispatch({ type: "InkColorChanged", value });
    },
  });
  const inkStyle = computed({
    get: () => model.value.inkStyle,
    set: (value: MapPathStyle) => {
      void dispatch({ type: "InkStyleChanged", value });
    },
  });
  const inkWeight = computed({
    get: () => model.value.inkWeight,
    set: (value: MapPathWeight) => {
      void dispatch({ type: "InkWeightChanged", value });
    },
  });
  const draftPoints = computed(() => model.value.draftPoints);
  const cursorWorld = computed(() => model.value.cursorWorld);
  const panning = computed(() => model.value.panning);
  const spaceHeld = computed(() => model.value.spaceHeld);
  const panStart = computed(() => model.value.panStart);
  const dragging = computed(() => model.value.dragging);
  const landmarkStamp = computed({
    get: () => model.value.landmarkStamp,
    set: (value: Maybe<string>) => {
      void dispatch({ type: "LandmarkStampChanged", value });
    },
  });
  const imageStamp = computed({
    get: () => model.value.imageStamp,
    set: (value: Maybe<string>) => {
      void dispatch({ type: "ImageStampChanged", value });
    },
  });
  const stampSize = computed(() => model.value.stampSize);
  const labelFont = computed({
    get: () => model.value.labelFont,
    set: (value: MapLabelFont) => {
      void dispatch({ type: "LabelFontChanged", value });
    },
  });
  const labelCurve = computed({
    get: () => model.value.labelCurve,
    set: (value: number) => {
      void dispatch({ type: "LabelCurveChanged", value });
    },
  });
  const labelSpacing = computed({
    get: () => model.value.labelSpacing,
    set: (value: number) => {
      void dispatch({ type: "LabelSpacingChanged", value });
    },
  });
  const labelRotation = computed({
    get: () => model.value.labelRotation,
    set: (value: number) => {
      void dispatch({ type: "LabelRotationChanged", value });
    },
  });
  const labelSize = computed({
    get: () => model.value.labelSize,
    set: (value: number) => {
      void dispatch({ type: "LabelSizeChanged", value });
    },
  });
  const hoverResize = computed(() => model.value.hoverResize);
  const fillHoverLoop = computed(() => model.value.fillHoverLoop);
  const pendingLabelClick = computed(() => model.value.pendingLabelClick);
  const pendingSelectClick = computed(() => model.value.pendingSelectClick);
  const labelDraft = computed({
    get: () => model.value.labelDraft,
    set: (value: string) => {
      void dispatch({ type: "LabelDraftChanged", value });
    },
  });
  const labelDialogOpen = computed(() => model.value.labelDialogOpen);
  const labelPlacement = computed(() => model.value.labelPlacement);
  const editingLabel = computed(() => model.value.editingLabel);
  const shortcutsOpen = computed(() => model.value.shortcutsOpen);
  const landmassBrushSize = computed({
    get: () => model.value.landmassBrushSize,
    set: (value: number) => {
      void dispatch({
        type: "LandmassBrushSizeChanged",
        value: clampLandmassBrushSize(value),
      });
    },
  });
  const landmassCoastRoughness = computed({
    get: () => model.value.landmassCoastRoughness,
    set: (value: number) => {
      void dispatch({
        type: "LandmassCoastRoughnessChanged",
        value: clampLandmassCoastRoughness(value),
      });
    },
  });
  const terrainBrushStyle = computed({
    get: () => model.value.terrainBrushStyle,
    set: (value: TerrainBrushStyle) => {
      void dispatch({ type: "TerrainBrushStyleChanged", value });
    },
  });
  const terrainCoverage = computed({
    get: () => model.value.terrainCoverage,
    set: (value: number) => {
      void dispatch({ type: "TerrainCoverageChanged", value });
    },
  });
  const terrainOpacity = computed({
    get: () => model.value.terrainOpacity,
    set: (value: number) => {
      void dispatch({ type: "TerrainOpacityChanged", value });
    },
  });
  const landmassSculpting = computed(() => model.value.landmassSculpting);
  const waterMode = computed({
    get: () => model.value.waterMode,
    set: (value: WaterMode) => {
      void dispatch({ type: "WaterModeChanged", value });
    },
  });
  const riverDraft = computed(() => model.value.riverDraft);
  const undoDepth = computed(() => model.value.undoStack.length);
  const redoDepth = computed(() => model.value.redoStack.length);

  let raf: Maybe<number> = nothing;
  /** Smooth zoom: wheel updates target; rAF eases viewport.zoom toward it. */
  let zoomRaf: Maybe<number> = nothing;
  let zoomTarget: Maybe<number> = nothing;
  let zoomFocusScreen: ScreenPoint = { x: 0, y: 0 };
  let zoomFocusWorld: WorldPoint = { x: 0, y: 0 };

  const activeLayer = computed(() => activeMapLayer(doc.value));
  /** Story path owns the canvas: geography/labels behave as locked until you leave the tool. */
  const storyPathLocksLayers = computed(() => tool.value === "storyPath");
  /** Same idea as labels: only select / stamp tools own landmark chrome. */
  const canInteractLandmarks = computed(
    () => tool.value === "select" || tool.value === "landmark",
  );
  const canInteractImages = computed(
    () => tool.value === "select" || tool.value === "image",
  );
  const canEditLayer = computed(
    () =>
      readonly.value === false &&
      activeLayer.value.locked === false &&
      storyPathLocksLayers.value === false,
  );
  const canEditLabels = computed(
    () =>
      readonly.value === false &&
      isLabelsLocked(doc.value) === false &&
      isLabelsVisible(doc.value) &&
      storyPathLocksLayers.value === false,
  );
  /**
   * Story path click focus for a place key (`landmark:id` / `marker:id`).
   * Kept separate from Selection so landmarks are not treated as map objects.
   */
  let storyPlaceFocusKey: Maybe<string> = nothing;

  const inkTargetSelected = computed(() => {
    const sel = selection.value;
    // Images have no ink; fills use the Fill picker.
    return (
      isJust(sel) && sel.value.kind !== "fill" && sel.value.kind !== "image"
    );
  });

  const fillTargetSelected = computed(
    () => isJust(selection.value) && selection.value.value.kind === "fill",
  );

  const sizeSliderKind = computed<Maybe<"landmark" | "label" | "image">>(() => {
    const sel = selection.value;
    if ((isJust(sel) && sel.value.kind === "label") || tool.value === "label")
      return just("label");
    if ((isJust(sel) && sel.value.kind === "image") || tool.value === "image")
      return just("image");
    if (
      (isJust(sel) && sel.value.kind === "landmark") ||
      tool.value === "landmark"
    )
      return just("landmark");
    return nothing;
  });

  const showResizeCursor = computed(() => {
    if (panning.value || spaceHeld.value) return false;
    const drag = dragging.value;
    if (
      isJust(drag) &&
      (drag.value.kind === "resize-landmark" ||
        drag.value.kind === "resize-image" ||
        drag.value.kind === "resize-label")
    )
      return true;
    return hoverResize.value;
  });

  const resizeCursor = computed(() => {
    if (tool.value === "crop") {
      const handle = isJust(model.value.cropHandle)
        ? model.value.cropHandle
        : isJust(cursorWorld.value)
          ? hitCropHandle(toScreen(cursorWorld.value.value))
          : nothing;
      return isJust(handle)
        ? landmarkHandleCursor(handle.value, 0)
        : "crosshair";
    }
    const image = selectedImage();
    if (isJust(image)) {
      const drag = dragging.value;
      const handle =
        isJust(drag) && drag.value.kind === "resize-image"
          ? just(drag.value.handle)
          : isJust(cursorWorld.value)
            ? hitMapImageHandle(
                image.value,
                cursorWorld.value.value,
                doc.value.viewport.zoom,
              )
            : nothing;
      return isJust(handle)
        ? landmarkHandleCursor(handle.value, image.value.rotation ?? 0)
        : "nwse-resize";
    }
    const landmark = selectedLandmark();
    if (isNothing(landmark)) {
      const label = selectedLabel();
      if (isNothing(label)) return "nwse-resize";
      const drag = dragging.value;
      const handle =
        isJust(drag) && drag.value.kind === "resize-label"
          ? just(drag.value.handle)
          : isJust(cursorWorld.value)
            ? hitLabelResizeHandle(
                toScreen(cursorWorld.value.value),
                label.value,
              )
            : nothing;
      return isJust(handle)
        ? landmarkHandleCursor(handle.value, label.value.rotation ?? 0)
        : "nwse-resize";
    }
    const drag = dragging.value;
    const handle =
      isJust(drag) && drag.value.kind === "resize-landmark"
        ? just(drag.value.handle)
        : isJust(cursorWorld.value)
          ? hitLandmarkHandle(
              landmark.value,
              cursorWorld.value.value,
              doc.value.viewport.zoom,
            )
          : nothing;
    return isJust(handle)
      ? landmarkHandleCursor(handle.value, landmark.value.rotation ?? 0)
      : "nwse-resize";
  });

  const sizeSliderModel = computed({
    get(): number {
      return isJust(sizeSliderKind.value) &&
        sizeSliderKind.value.value === "label"
        ? labelSize.value
        : stampSize.value;
    },
    set(value: number): void {
      if (
        isJust(sizeSliderKind.value) &&
        sizeSliderKind.value.value === "label"
      ) {
        void dispatch({ type: "LabelSizeChanged", value });
        applyLabelSize();
      } else {
        void dispatch({ type: "StampSizeChanged", value });
        applyStampSize();
      }
    },
  });

  function cloneMaskSnapshot(): Maybe<ImageData> {
    if (isNothing(landmassCanvas)) return nothing;
    return cloneCanvasImageData(landmassCanvas.value);
  }

  function restoreMaskSnapshot(data: Maybe<ImageData>): void {
    const lm = doc.value.landmass;
    if (isNothing(data)) {
      if (typeof lm === "undefined") {
        landmassCanvas = nothing;
        invalidateCoasts();
        return;
      }
      landmassCanvas = just(createEmptyLandmassCanvas(lm.width, lm.height));
      invalidateCoasts();
      return;
    }
    if (isNothing(landmassCanvas)) {
      landmassCanvas = just(
        createEmptyLandmassCanvas(data.value.width, data.value.height),
      );
    }
    applyCanvasImageData(landmassCanvas.value, data.value);
    invalidateCoasts();
  }

  function pushUndo(): void {
    void dispatch({
      type: "UndoRecorded",
      snapshot: serializeMapDocument(doc.value),
    });
    maskUndoStack.push(cloneMaskSnapshot());
    if (maskUndoStack.length > 50) maskUndoStack.shift();
    maskRedoStack.length = 0;
  }

  async function undo(): Promise<void> {
    if (readonly.value) return;
    if (model.value.undoStack.length === 0) return;
    maskRedoStack.push(cloneMaskSnapshot());
    const popped = maskUndoStack.pop();
    const prev: Maybe<ImageData> =
      typeof popped === "undefined" ? nothing : popped;
    // Restore the JSON doc first, then the mask, so a path-watch reload (if
    // any) cannot race ahead of the snapshot we are restoring.
    await dispatch({
      type: "UndoRequested",
      snapshot: serializeMapDocument(doc.value),
    });
    restoreMaskSnapshot(prev);
    ensureCoasts();
    options.onLandmassDirty?.();
    scheduleDraw();
  }

  async function redo(): Promise<void> {
    if (readonly.value) return;
    if (model.value.redoStack.length === 0) return;
    maskUndoStack.push(cloneMaskSnapshot());
    const popped = maskRedoStack.pop();
    const next: Maybe<ImageData> =
      typeof popped === "undefined" ? nothing : popped;
    await dispatch({
      type: "RedoRequested",
      snapshot: serializeMapDocument(doc.value),
    });
    restoreMaskSnapshot(next);
    ensureCoasts();
    options.onLandmassDirty?.();
    scheduleDraw();
  }

  function ensureLandmass(): Maybe<{
    canvas: HTMLCanvasElement;
    landmass: MapLandmass;
    ctx: CanvasRenderingContext2D;
  }> {
    let landmass = doc.value.landmass;
    if (typeof landmass === "undefined") {
      landmass = createDefaultLandmass(`Assets/landmass-${newMapId()}.png`);
      doc.value.landmass = landmass;
    }
    if (
      isNothing(landmassCanvas) ||
      landmassCanvas.value.width !== landmass.width ||
      landmassCanvas.value.height !== landmass.height
    ) {
      landmassCanvas = just(
        createEmptyLandmassCanvas(landmass.width, landmass.height),
      );
    }
    const page = mapPage(doc.value);
    const world = landmass.world;
    const left = Math.min(world.x, (page.centerX ?? 0) - page.width / 2);
    const top = Math.min(world.y, (page.centerY ?? 0) - page.height / 2);
    const right = Math.max(
      world.x + world.w,
      (page.centerX ?? 0) + page.width / 2,
    );
    const bottom = Math.max(
      world.y + world.h,
      (page.centerY ?? 0) + page.height / 2,
    );
    if (
      left !== world.x ||
      top !== world.y ||
      right !== world.x + world.w ||
      bottom !== world.y + world.h
    ) {
      const pixelWidth = world.w / landmass.width;
      const pixelHeight = world.h / landmass.height;
      const offsetX = Math.ceil((world.x - left) / pixelWidth);
      const offsetY = Math.ceil((world.y - top) / pixelHeight);
      const width = Math.ceil((right - world.x) / pixelWidth) + offsetX;
      const height = Math.ceil((bottom - world.y) / pixelHeight) + offsetY;
      const expanded = createEmptyLandmassCanvas(width, height);
      const expandedContext = expanded.getContext("2d");
      if (expandedContext === null) return nothing;
      expandedContext.drawImage(landmassCanvas.value, offsetX, offsetY);
      landmass.world = {
        x: world.x - offsetX * pixelWidth,
        y: world.y - offsetY * pixelHeight,
        w: width * pixelWidth,
        h: height * pixelHeight,
      };
      landmass.width = width;
      landmass.height = height;
      landmassCanvas = just(expanded);
      invalidateCoasts();
    }
    const ctx = landmassCanvas.value.getContext("2d");
    if (ctx === null) return nothing;
    return just({ canvas: landmassCanvas.value, landmass, ctx });
  }

  function paintLandmassAt(world: WorldPoint): void {
    const ensured = ensureLandmass();
    if (isNothing(ensured)) return;
    const { ctx, landmass } = ensured.value;
    if (isLandmassLocked(landmass) === true) return;
    if (isJust(lastLandmassPaint)) {
      const prev = lastLandmassPaint.value;
      const radiusWorld = landmassBrushSize.value / 2;
      const dist = Math.hypot(world.x - prev.x, world.y - prev.y);
      if (dist < radiusWorld * 0.35) return;
      const steps = Math.max(1, Math.ceil(dist / (radiusWorld * 0.35)));
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        stampLandmassBrush(
          ctx,
          landmass,
          {
            x: prev.x + (world.x - prev.x) * t,
            y: prev.y + (world.y - prev.y) * t,
          },
          landmassBrushSize.value,
          activeSculptMode,
          landmassCoastRoughness.value,
          fillColor.value,
          {
            style: terrainBrushStyle.value,
            opacity: terrainOpacity.value,
            coverage: terrainCoverage.value,
            strokeSeed: terrainStrokeSeed,
          },
        );
      }
    } else {
      stampLandmassBrush(
        ctx,
        landmass,
        world,
        landmassBrushSize.value,
        activeSculptMode,
        landmassCoastRoughness.value,
        fillColor.value,
        {
          style: terrainBrushStyle.value,
          opacity: terrainOpacity.value,
          coverage: terrainCoverage.value,
          strokeSeed: terrainStrokeSeed,
        },
      );
    }
    lastLandmassPaint = just(world);
    // Rebuild geometry after the stroke, never in the pointer-move handler.
    if (activeSculptMode !== "paint") invalidateCoasts();
  }

  /** World threshold for attaching a tributary to an existing river draft. */
  function riverForkThresholdWorld(): number {
    return Math.max(
      landmassBrushSize.value * 0.45,
      joinThresholdWorld(doc.value.viewport.zoom) * 2,
    );
  }

  function cancelRiverDraft(): void {
    void dispatch({ type: "RiverDraftChanged", value: emptyRiverDraft() });
    void dispatch({ type: "CursorWorldChanged", value: nothing });
    scheduleDraw();
  }

  function finishRiverDraft(): void {
    const draft = riverDraft.value;
    const branches = draft.branches.filter((b) => b.length >= 2);
    if (branches.length === 0) {
      cancelRiverDraft();
      return;
    }
    const ensured = ensureLandmass();
    if (isNothing(ensured)) {
      cancelRiverDraft();
      return;
    }
    const { ctx, landmass } = ensured.value;
    if (isLandmassLocked(landmass) === true) {
      cancelRiverDraft();
      return;
    }
    // Undo was pushed on the first click of this draft.
    for (const branch of branches) {
      stampRiverCorridor(
        ctx,
        landmass,
        branch,
        landmassBrushSize.value,
        landmassCoastRoughness.value,
      );
    }
    invalidateCoasts();
    ensureCoasts();
    options.onLandmassDirty?.();
    cancelRiverDraft();
  }

  function addRiverDraftPoint(worldRaw: WorldPoint): void {
    const draft = riverDraft.value;
    if (draft.branches.length === 0) {
      pushUndo();
      void dispatch({
        type: "RiverDraftChanged",
        value: { branches: [[worldRaw]], active: 0 },
      });
      return;
    }

    const threshold = riverForkThresholdWorld();
    const fork = findRiverForkAttach(worldRaw, draft.branches, threshold);
    if (isJust(fork)) {
      const activeBranch = atIndexMaybe(draft.branches, draft.active);
      const tipM =
        isJust(activeBranch) && activeBranch.value.length > 0
          ? atIndexMaybe(activeBranch.value, activeBranch.value.length - 1)
          : nothing;
      const nearOwnTip =
        isJust(tipM) &&
        fork.value.branchIndex === draft.active &&
        distance(fork.value.point, tipM.value) < threshold * 0.5;
      if (nearOwnTip === false) {
        const nextBranches = draft.branches.map((b) => [...b]);
        const junction = fork.value.point;
        const tributary =
          distance(junction, worldRaw) < 1 ? [junction] : [junction, worldRaw];
        nextBranches.push(tributary);
        void dispatch({
          type: "RiverDraftChanged",
          value: {
            branches: nextBranches,
            active: nextBranches.length - 1,
          },
        });
        return;
      }
    }

    const branches = draft.branches.map((b) => [...b]);
    let active = draft.active;
    const existing = atIndexMaybe(branches, active);
    if (isNothing(existing)) {
      active = branches.length;
      branches.push([worldRaw]);
    } else {
      const last = atIndexMaybe(existing.value, existing.value.length - 1);
      if (
        isJust(last) &&
        last.value.x === worldRaw.x &&
        last.value.y === worldRaw.y
      ) {
        return;
      }
      branches[active] = [...existing.value, worldRaw];
    }
    void dispatch({
      type: "RiverDraftChanged",
      value: { branches, active },
    });
  }

  function beginLandmassStroke(
    mode: LandmassBrushMode,
    worldRaw: WorldPoint,
    pointerId: number,
  ): void {
    activeSculptMode = mode;
    if (mode === "paint")
      terrainStrokeSeed = Math.floor(Math.random() * 2147483647);
    pushUndo();
    lastLandmassPaint = nothing;
    void dispatch({ type: "LandmassSculptStarted" });
    paintLandmassAt(worldRaw);
    void dispatch({ type: "CursorWorldChanged", value: just(worldRaw) });
    if (isJust(canvasEl.value))
      canvasEl.value.value.setPointerCapture(pointerId);
    scheduleDraw();
  }

  function beginEraseStroke(worldRaw: WorldPoint, pointerId: number): void {
    activeSculptMode = "erase";
    pushUndo();
    lastLandmassPaint = nothing;
    void dispatch({ type: "LandmassSculptStarted" });
    if (typeof doc.value.landmass !== "undefined" || isJust(landmassCanvas)) {
      paintLandmassAt(worldRaw);
    }
    eraseObjectsUnderBrush(worldRaw);
    void dispatch({ type: "CursorWorldChanged", value: just(worldRaw) });
    if (isJust(canvasEl.value))
      canvasEl.value.value.setPointerCapture(pointerId);
    scheduleDraw();
  }

  /** Remove landmarks / nodes / paths / fills / labels under the erase brush. */
  function eraseObjectsUnderBrush(world: WorldPoint): void {
    if (canEditLayer.value === false) return;
    const radius = landmassBrushSize.value / 2;
    let changed = false;
    for (const layer of doc.value.layers) {
      if (layer.visible === false || layer.locked) continue;
      const beforeLandmarks = layer.landmarks.length;
      layer.landmarks = layer.landmarks.filter((lm) => {
        const size = landmarkDrawSize(lm, 1);
        const local = landmarkLocalPoint(lm, world);
        const dx = Math.max(0, Math.abs(local.x) - size.width / 2);
        const dy = Math.max(0, Math.abs(local.y) - size.height / 2);
        return Math.hypot(dx, dy) > radius;
      });
      if (layer.landmarks.length !== beforeLandmarks) changed = true;

      const beforeImages = layer.images.length;
      layer.images = layer.images.filter((img) => {
        const size = mapImageDrawSize(img, 1);
        const local = mapImageLocalPoint(img, world);
        const dx = Math.max(0, Math.abs(local.x) - size.width / 2);
        const dy = Math.max(0, Math.abs(local.y) - size.height / 2);
        return Math.hypot(dx, dy) > radius;
      });
      if (layer.images.length !== beforeImages) changed = true;

      const beforeFills = layer.fills.length;
      layer.fills = layer.fills.filter((fill) => {
        const points = fillPoints(layer, fill);
        if (points.length < 3) return false;
        if (pointInPolygon(world, points)) return false;
        return points.every((p) => distance(p, world) > radius);
      });
      if (layer.fills.length !== beforeFills) changed = true;

      const removedNodeIds = new Set<string>();
      for (const node of layer.nodes) {
        if (distance(node, world) <= radius) removedNodeIds.add(node.id);
      }
      if (removedNodeIds.size > 0) {
        changed = true;
        for (const path of layer.paths) {
          path.nodeIds = path.nodeIds.filter(
            (id) => removedNodeIds.has(id) === false,
          );
        }
      }
      const beforePaths = layer.paths.length;
      layer.paths = layer.paths.filter((path) => {
        if (path.nodeIds.length < 2) return false;
        const points = pathPoints(layer, path);
        for (const p of points) {
          if (distance(p, world) <= radius) return false;
        }
        return true;
      });
      if (layer.paths.length !== beforePaths) changed = true;
      if (removedNodeIds.size > 0 || layer.paths.length !== beforePaths) {
        pruneOrphanNodes(layer);
      }
    }
    if (
      isLabelsLocked(doc.value) === false &&
      isLabelsVisible(doc.value) === true
    ) {
      const beforeLabels = doc.value.labels.length;
      doc.value.labels = doc.value.labels.filter((label) => {
        const box = labelSelectionBox(label);
        const point = toScreen(world);
        const screenRadius = radius * doc.value.viewport.zoom;
        return (
          point.x + screenRadius < box.x ||
          point.x - screenRadius > box.x + box.w ||
          point.y + screenRadius < box.y ||
          point.y - screenRadius > box.y + box.h
        );
      });
      if (doc.value.labels.length !== beforeLabels) changed = true;
    }
    if (changed === false) return;
    const sel = selection.value;
    if (isNothing(sel)) return;
    const target = sel.value;
    const alive = ((): boolean => {
      switch (target.kind) {
        case "label":
          return doc.value.labels.some((l) => l.id === target.id);
        case "storyMarker":
          return doc.value.storyMarkers.some((m) => m.id === target.id);
        case "image":
        case "landmark":
        case "fill":
        case "path":
        case "node": {
          const layerM = layerById(target.layerId);
          if (isNothing(layerM)) return false;
          const layer = layerM.value;
          if (target.kind === "image")
            return layer.images.some((img) => img.id === target.id);
          if (target.kind === "landmark")
            return layer.landmarks.some((l) => l.id === target.id);
          if (target.kind === "fill")
            return layer.fills.some((f) => f.id === target.id);
          if (target.kind === "path")
            return layer.paths.some((p) => p.id === target.pathId);
          return layer.nodes.some((n) => n.id === target.nodeId);
        }
        default:
          return assertNever(target);
      }
    })();
    if (alive === false) {
      void dispatch({ type: "SelectionChanged", value: nothing });
    }
  }

  function setLandmassFromImage(img: Maybe<HTMLImageElement>): void {
    const lm = doc.value.landmass;
    if (isNothing(img) || typeof lm === "undefined") {
      if (typeof lm === "undefined") landmassCanvas = nothing;
      invalidateCoasts();
      scheduleDraw();
      return;
    }
    const canvas = createEmptyLandmassCanvas(lm.width, lm.height);
    const ctx = canvas.getContext("2d");
    if (ctx === null) return;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img.value, 0, 0, lm.width, lm.height);
    landmassCanvas = just(canvas);
    invalidateCoasts();
    scheduleDraw();
  }

  function getLandmassCanvas(): Maybe<HTMLCanvasElement> {
    return landmassCanvas;
  }

  function getLandmassCoasts(): CoastPolyline[] {
    return ensureCoasts();
  }

  function cancelDraftLine(): void {
    void dispatch({ type: "DraftCancelled" });
  }

  function setTool(next: MapTool): void {
    if (labelDialogOpen.value) commitLabel();
    if (next !== "storyPath") storyPlaceFocusKey = nothing;
    void dispatch({ type: "ToolSelected", tool: next });
  }

  function adoptSelectTool(): void {
    void dispatch({ type: "SelectToolAdopted" });
  }

  // Coalesce pointer moves and Vue watchers into one repaint per browser frame.
  function scheduleDraw(): void {
    if (isJust(raf)) return;
    raf = just(
      requestAnimationFrame(() => {
        raf = nothing;
        draw();
      }),
    );
  }

  function canvasSize(): { w: number; h: number } {
    const canvasM = canvasEl.value;
    if (isNothing(canvasM)) return { w: 0, h: 0 };
    const canvas = canvasM.value;
    return { w: canvas.clientWidth, h: canvas.clientHeight };
  }

  function toScreen(p: WorldPoint): ScreenPoint {
    const { w, h } = canvasSize();
    return worldToScreen(p, doc.value.viewport, w, h);
  }

  function toWorld(p: ScreenPoint): WorldPoint {
    const { w, h } = canvasSize();
    return screenToWorld(p, doc.value.viewport, w, h);
  }

  function landmarkById(layerId: string, id: string): Maybe<MapLandmark> {
    return andThenMaybe(layerById(layerId), (layer) =>
      toMaybe(layer.landmarks.find((l) => l.id === id)),
    );
  }

  function labelById(id: string): Maybe<MapLabel> {
    return toMaybe(doc.value.labels.find((l) => l.id === id));
  }

  function selectedLandmark(): Maybe<MapLandmark> {
    const sel = selection.value;
    if (isNothing(sel) || sel.value.kind !== "landmark") return nothing;
    const { layerId, id } = sel.value;
    return landmarkById(layerId, id);
  }

  function imageById(layerId: string, id: string): Maybe<MapImage> {
    return andThenMaybe(layerById(layerId), (layer) =>
      toMaybe(layer.images.find((img) => img.id === id)),
    );
  }

  function selectedImage(): Maybe<MapImage> {
    const sel = selection.value;
    if (isNothing(sel) || sel.value.kind !== "image") return nothing;
    const { layerId, id } = sel.value;
    return imageById(layerId, id);
  }

  /** Focus a landmark from outside the canvas (e.g. Relationships → map). */
  function selectLandmarkById(landmarkId: string): boolean {
    const id = landmarkId.trim();
    if (id.length === 0) return false;
    for (const layer of doc.value.layers) {
      const landmark = toMaybe(layer.landmarks.find((lm) => lm.id === id));
      if (isNothing(landmark)) continue;
      doc.value.viewport = {
        x: landmark.value.x,
        y: landmark.value.y,
        zoom: 1,
      };
      void dispatch({ type: "LandmarkFocused", layerId: layer.id, id });
      scheduleDraw();
      return true;
    }
    const marker = toMaybe(
      doc.value.storyMarkers.find((item) => item.id === id),
    );
    if (isJust(marker)) {
      doc.value.viewport = { x: marker.value.x, y: marker.value.y, zoom: 1 };
      void dispatch({ type: "StoryMarkerFocused", id });
      scheduleDraw();
      return true;
    }
    return false;
  }

  function selectedPath(): Maybe<MapPath> {
    const sel = selection.value;
    if (
      isNothing(sel) ||
      (sel.value.kind !== "path" && sel.value.kind !== "node")
    )
      return nothing;
    const { layerId, pathId } = sel.value;
    if (pathId.length === 0) return nothing;
    return andThenMaybe(layerById(layerId), (layer) =>
      toMaybe(layer.paths.find((p) => p.id === pathId)),
    );
  }

  function selectedLabel(): Maybe<MapLabel> {
    const sel = selection.value;
    if (isNothing(sel) || sel.value.kind !== "label") return nothing;
    return labelById(sel.value.id);
  }

  function selectedFill(): Maybe<MapFill> {
    const sel = selection.value;
    if (isNothing(sel) || sel.value.kind !== "fill") return nothing;
    const { layerId, id } = sel.value;
    return andThenMaybe(layerById(layerId), (layer) =>
      toMaybe(layer.fills.find((f) => f.id === id)),
    );
  }

  function updateFillHover(world: WorldPoint): void {
    if (tool.value !== "fill" || canEditLayer.value === false) {
      void dispatch({ type: "FillHoverLoopChanged", value: nothing });
      return;
    }
    const loop = findFillLoop(activeLayer.value, world);
    void dispatch({
      type: "FillHoverLoopChanged",
      value: loop,
    });
  }

  /** True when the world point sits on land in the mask (if any). */
  function isClickOnLand(world: WorldPoint): boolean {
    const lm = doc.value.landmass;
    if (typeof lm === "undefined" || isNothing(landmassCanvas)) return false;
    const canvas = landmassCanvas.value;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (ctx === null) return false;
    const { x, y } = worldToMaskPixel(world, lm);
    const px = Math.floor(x);
    const py = Math.floor(y);
    if (px < 0 || py < 0 || px >= canvas.width || py >= canvas.height) {
      return false;
    }
    const { data } = ctx.getImageData(px, py, 1, 1);
    return (data[3] ?? 0) >= 128;
  }

  /** Recolor a contiguous same-color land region under the paint bucket. */
  function applyTerrainFillAt(world: WorldPoint): boolean {
    const lm = doc.value.landmass;
    if (
      typeof lm === "undefined" ||
      isNothing(landmassCanvas) ||
      isLandmassLocked(lm) === true
    ) {
      return false;
    }
    const canvas = landmassCanvas.value;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return false;
    const { x, y } = worldToMaskPixel(world, lm);
    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const changed = floodFillLandColor(
      image.data,
      canvas.width,
      canvas.height,
      x,
      y,
      cssColorToRgb(fillColor.value),
    );
    if (changed === false) return false;
    pushUndo();
    ctx.putImageData(image, 0, 0);
    // Silhouette unchanged; only tint. Still mark dirty for asset write.
    options.onLandmassDirty?.();
    scheduleDraw();
    return true;
  }

  /** Paint-bucket the sea / paper behind land (no path required). */
  function applyPaperFill(): void {
    if (isPaperLocked(doc.value) === true) return;
    const current = mapPaperColor(doc.value);
    if (current.toLowerCase() === fillColor.value.toLowerCase()) return;
    pushUndo();
    const stored = storedMapPaper(fillColor.value);
    if (isNothing(stored)) delete doc.value.paperColor;
    else doc.value.paperColor = stored.value;
    scheduleDraw();
  }

  function applyPathLoopFill(world: WorldPoint): boolean {
    const layer = activeLayer.value;
    const loop = findFillLoop(layer, world);
    if (loop.tag !== "just") return false;
    const key = cycleKey(loop.value);
    const existingFill = toMaybe(
      layer.fills.find((fill) => cycleKey(fill.nodeIds) === key),
    );
    if (isJust(existingFill)) {
      const existing = existingFill.value;
      if (existing.color === fillColor.value) {
        void dispatch({
          type: "SelectionChanged",
          value: just({ kind: "fill", layerId: layer.id, id: existing.id }),
        });
        return true;
      }
      pushUndo();
      existing.color = fillColor.value;
      void dispatch({
        type: "SelectionChanged",
        value: just({ kind: "fill", layerId: layer.id, id: existing.id }),
      });
      scheduleDraw();
      return true;
    }
    pushUndo();
    const fill: MapFill = {
      id: newMapId(),
      nodeIds: loop.value,
      color: fillColor.value,
    };
    layer.fills.push(fill);
    void dispatch({
      type: "SelectionChanged",
      value: just({ kind: "fill", layerId: layer.id, id: fill.id }),
    });
    scheduleDraw();
    return true;
  }

  function applyFillAt(world: WorldPoint): void {
    // Enclosed borders take precedence so political overlays can sit above land.
    if (applyPathLoopFill(world)) return;
    if (isClickOnLand(world)) {
      void applyTerrainFillAt(world);
      return;
    }
    applyPaperFill();
  }

  function measureLabelWidth(label: MapLabel, fontPx: number): number {
    const context = andThenMaybe(canvasEl.value, (canvas) =>
      toMaybe(canvas.getContext("2d")),
    );
    if (isNothing(context)) return label.text.length * fontPx * 0.52;
    const ctx = context.value;
    ctx.font = labelCanvasFont(fontPx, label.font);
    return Math.max(
      ...label.text.split("\n").map((line) => ctx.measureText(line).width),
    );
  }

  function labelSelectionBox(label: MapLabel): {
    x: number;
    y: number;
    w: number;
    h: number;
    handle: ScreenPoint;
  } {
    const p = toScreen(label);
    const context = andThenMaybe(canvasEl.value, (canvas) =>
      toMaybe(canvas.getContext("2d")),
    );
    const layout = isJust(context)
      ? measureMapLabel(context.value, label, doc.value.viewport.zoom)
      : { minX: -20, minY: -10, width: 40, height: 20 };
    const w = layout.width + 8,
      h = layout.height + 8;
    const x = p.x + layout.minX - 4,
      y = p.y + layout.minY - 4;
    return { x, y, w, h, handle: { x: x + w, y: y + h } };
  }

  function labelTransformBox(label: MapLabel): {
    width: number;
    height: number;
    corners: ScreenPoint[];
    handles: { id: LandmarkHandle; point: ScreenPoint }[];
  } {
    const box = labelSelectionBox({ ...label, rotation: 0 });
    const center = toScreen(label);
    const point = (x: number, y: number): ScreenPoint => {
      const offset = rotatePoint({ x, y }, label.rotation ?? 0);
      return { x: center.x + offset.x, y: center.y + offset.y };
    };
    return {
      width: box.w,
      height: box.h,
      corners: LANDMARK_HANDLES.filter((h) => h.x !== 0 && h.y !== 0).map((h) =>
        point((h.x * box.w) / 2, (h.y * box.h) / 2),
      ),
      handles: [
        ...LANDMARK_HANDLES.map((h) => ({
          id: h.id as LandmarkHandle,
          point: point((h.x * box.w) / 2, (h.y * box.h) / 2),
        })),
        { id: "rotate" as const, point: point(0, -box.h / 2 - 26) },
      ],
    };
  }

  function hitLabelResizeHandle(
    screen: ScreenPoint,
    label: MapLabel,
  ): Maybe<LandmarkHandle> {
    const handle = toMaybe(
      labelTransformBox(label).handles.find(
        (h) => screenDist(screen, h.point) <= 8,
      ),
    );
    return isJust(handle) ? just(handle.value.id) : nothing;
  }

  function overResizeHandle(screen: ScreenPoint): boolean {
    if (readonly.value) return false;
    if (tool.value === "crop") return isJust(hitCropHandle(screen));
    const image = selectedImage();
    if (
      canInteractImages.value &&
      canEditLayer.value &&
      isJust(image) &&
      isJust(
        hitMapImageHandle(
          image.value,
          toWorld(screen),
          doc.value.viewport.zoom,
        ),
      )
    )
      return true;
    const landmark = selectedLandmark();
    if (
      canInteractLandmarks.value &&
      canEditLayer.value &&
      isJust(landmark) &&
      isJust(
        hitLandmarkHandle(
          landmark.value,
          toWorld(screen),
          doc.value.viewport.zoom,
        ),
      )
    )
      return true;
    const label = selectedLabel();
    return (
      canEditLabels.value &&
      isJust(label) &&
      isJust(hitLabelResizeHandle(screen, label.value))
    );
  }

  function beginHandleResize(screen: ScreenPoint): boolean {
    const image = selectedImage();
    if (
      canEditLayer.value &&
      isJust(image) &&
      isJust(
        hitMapImageHandle(
          image.value,
          toWorld(screen),
          doc.value.viewport.zoom,
        ),
      )
    ) {
      adoptSelectTool();
      const handle = hitMapImageHandle(
        image.value,
        toWorld(screen),
        doc.value.viewport.zoom,
      );
      if (isJust(handle)) beginImageResize(image.value, screen, handle.value);
      return true;
    }
    const landmark = selectedLandmark();
    if (
      canEditLayer.value &&
      isJust(landmark) &&
      isJust(
        hitLandmarkHandle(
          landmark.value,
          toWorld(screen),
          doc.value.viewport.zoom,
        ),
      )
    ) {
      adoptSelectTool();
      const handle = hitLandmarkHandle(
        landmark.value,
        toWorld(screen),
        doc.value.viewport.zoom,
      );
      if (isJust(handle))
        beginLandmarkResize(landmark.value, screen, handle.value);
      return true;
    }
    const label = selectedLabel();
    if (
      canEditLabels.value &&
      isJust(label) &&
      isJust(hitLabelResizeHandle(screen, label.value))
    ) {
      adoptSelectTool();
      const handle = hitLabelResizeHandle(screen, label.value);
      if (isJust(handle)) beginLabelResize(label.value, screen, handle.value);
      return true;
    }
    return false;
  }

  function hitTestLabel(label: MapLabel, world: WorldPoint): boolean {
    const point = toScreen(world);
    const box = labelSelectionBox(label);
    return (
      point.x >= box.x &&
      point.x <= box.x + box.w &&
      point.y >= box.y &&
      point.y <= box.y + box.h
    );
  }

  function layerById(id: string): Maybe<MapLayer> {
    return toMaybe(doc.value.layers.find((l) => l.id === id));
  }

  function draw(): void {
    const canvasM = canvasEl.value;
    if (isNothing(canvasM)) return;
    const canvas = canvasM.value;
    const { w, h } = canvasSize();
    if (w <= 0 || h <= 0) return;
    // CSS pixels describe pointer positions and all drawing below. The backing
    // bitmap has extra pixels on Retina screens; scale the context once so the
    // rest of the renderer never needs to multiply coordinates by DPR.
    const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const context = toMaybe(canvas.getContext("2d"));
    if (isNothing(context)) return;
    const ctx = context.value;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const page = mapPage(doc.value);
    const topLeft = toScreen({
      x: (page.centerX ?? 0) - page.width / 2,
      y: (page.centerY ?? 0) - page.height / 2,
    });
    const pageWidth = page.width * doc.value.viewport.zoom;
    const pageHeight = page.height * doc.value.viewport.zoom;
    const workspaceColor = getComputedStyle(canvas)
      .getPropertyValue("--bg")
      .trim();
    ctx.fillStyle = workspaceColor.length > 0 ? workspaceColor : "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.save();
    ctx.shadowColor = "rgba(0, 0, 0, 0.25)";
    ctx.shadowBlur = 12;
    ctx.fillStyle = mapPaperColor(doc.value);
    ctx.fillRect(topLeft.x, topLeft.y, pageWidth, pageHeight);
    ctx.restore();
    ctx.save();
    ctx.beginPath();
    ctx.rect(topLeft.x, topLeft.y, pageWidth, pageHeight);
    ctx.clip();
    if (isPaperVisible(doc.value) === true) {
      const paper = mapPaperColor(doc.value);
      const gridLine = "rgba(60, 50, 40, 0.08)";
      drawGrid(ctx, doc.value, w, h, paper, gridLine);
    } else {
      // No ocean: white workspace with a slightly stronger grid (not chrome bg).
      const gridLine = "rgba(60, 50, 40, 0.12)";
      drawGrid(ctx, doc.value, w, h, "#ffffff", gridLine);
    }

    // Canvas is painted back to front: later strokes cover earlier ones.
    // Landmass sits below biome fills; placeable images sit under landmarks.
    drawLandmass(ctx);
    drawMapPaperGrain(ctx, toScreen, w, h);
    for (const layer of doc.value.layers) {
      if (layer.visible === false) continue;
      paintLayer(ctx, layer.opacity ?? 1, (context) => {
        drawFills(context, layer, false);
        drawPaths(context, layer, false);
        drawImages(context, layer, false);
        drawLandmarks(context, layer, false);
      });
    }
    for (const layer of doc.value.layers) {
      if (layer.visible === false) continue;
      drawFills(ctx, layer, true);
      drawPaths(ctx, layer, true);
      drawImages(ctx, layer, true);
      drawLandmarks(ctx, layer, true);
    }
    drawFillPreview(ctx);
    drawLabels(ctx);
    drawStoryPathOverlay(ctx);
    drawMapFrame(ctx, page, toScreen, doc.value.viewport.zoom);
    drawNodeHandles(ctx);
    drawToolPreviews(ctx);
    ctx.restore();
    const crop = model.value.cropDraft;
    if (isJust(crop)) {
      ctx.save();
      const a = toScreen(crop.value.start),
        b = toScreen(crop.value.end);
      const x = Math.min(a.x, b.x),
        y = Math.min(a.y, b.y);
      const width = Math.abs(b.x - a.x),
        height = Math.abs(b.y - a.y);
      ctx.fillStyle = "rgba(0, 0, 0, 0.35)";
      ctx.beginPath();
      ctx.rect(topLeft.x, topLeft.y, pageWidth, pageHeight);
      ctx.rect(x, y, width, height);
      ctx.fill("evenodd");
      ctx.strokeStyle = "#faf8f0";
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(x, y, width, height);
      ctx.setLineDash([]);
      ctx.fillStyle = "#faf8f0";
      ctx.strokeStyle = "#51483c";
      for (const handle of cropHandles()) {
        ctx.fillRect(handle.point.x - 3.5, handle.point.y - 3.5, 7, 7);
        ctx.strokeRect(handle.point.x - 3.5, handle.point.y - 3.5, 7, 7);
      }
      ctx.restore();
    }
  }

  function drawLandmass(ctx: CanvasRenderingContext2D): void {
    const lm = doc.value.landmass;
    if (
      typeof lm === "undefined" ||
      isNothing(landmassCanvas) ||
      isLandmassVisible(lm) === false
    ) {
      return;
    }
    if (landmassSculpting.value === true) {
      drawLandmassPreview(ctx, landmassCanvas.value, lm, toScreen);
      return;
    }
    drawLandmassMask(ctx, landmassCanvas.value, lm, toScreen, ensureCoasts());
  }

  const paintLayer = createMapLayerPainter();

  function drawFills(
    ctx: CanvasRenderingContext2D,
    layer: MapLayer,
    overlay: boolean,
  ): void {
    for (const fill of fillsBackToFront(layer)) {
      const points = fillPoints(layer, fill);
      if (points.length < 3) continue;
      if (overlay === false) drawMapFill(ctx, points, fill.color, toScreen);
      if (
        overlay &&
        isJust(selection.value) &&
        selection.value.value.kind === "fill" &&
        selection.value.value.layerId === layer.id &&
        selection.value.value.id === fill.id
      ) {
        ctx.strokeStyle = "rgba(180, 120, 40, 0.7)";
        ctx.lineWidth = 1.5;
        traceSmoothPath(ctx, points, toScreen, true);
        ctx.stroke();
      }
    }
  }

  function drawFillPreview(ctx: CanvasRenderingContext2D): void {
    if (
      isJust(fillHoverLoop.value) &&
      canEditLayer.value &&
      tool.value === "fill"
    ) {
      const points = fillPoints(activeLayer.value, {
        nodeIds: fillHoverLoop.value.value,
      });
      if (points.length >= 3) {
        drawMapFill(
          ctx,
          points,
          fillColor.value,
          toScreen,
          MAP_FILL_HOVER_ALPHA,
        );
      }
    }
  }

  function drawPaths(
    ctx: CanvasRenderingContext2D,
    layer: MapLayer,
    overlay: boolean,
  ): void {
    for (const path of layer.paths) {
      const points = pathPoints(layer, path);
      if (points.length < 2) continue;
      const sel = selection.value;
      const selected =
        isJust(sel) &&
        (sel.value.kind === "path" || sel.value.kind === "node") &&
        sel.value.layerId === layer.id &&
        sel.value.pathId === path.id;
      if (overlay === false) {
        ctx.strokeStyle = path.color;
        ctx.lineWidth = pathLineWidth(path.weight, doc.value.viewport.zoom);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.setLineDash(pathLineDash(path.style ?? "solid", ctx.lineWidth));
        traceSmoothPath(ctx, points, toScreen);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      if (overlay && selected) {
        ctx.strokeStyle = "rgba(180, 120, 40, 0.55)";
        ctx.lineWidth = pathLineWidth(path.weight, doc.value.viewport.zoom) + 4;
        traceSmoothPath(ctx, points, toScreen);
        ctx.stroke();
      }
    }
  }

  function storyPathSelectedKey(): Maybe<string> {
    if (isJust(storyPlaceFocusKey)) return storyPlaceFocusKey;
    const sel = selection.value;
    if (isNothing(sel)) return nothing;
    if (sel.value.kind === "storyMarker") {
      return just(storyPathPlaceKey("marker", sel.value.id));
    }
    return nothing;
  }

  function drawStoryPathOverlay(ctx: CanvasRenderingContext2D): void {
    if (tool.value !== "storyPath") return;
    const stops = buildStoryPath(doc.value, sceneOrder.value);
    const selectedKey = storyPathSelectedKey();
    const highlightKeys = options.storyHighlightKeys?.value ?? [];
    drawStoryPath(ctx, stops, toScreen, {
      zoom: doc.value.viewport.zoom,
      ...(isJust(selectedKey) ? { selectedKey: selectedKey.value } : {}),
      ...(highlightKeys.length > 0 ? { highlightedKeys: highlightKeys } : {}),
    });
  }

  const storyPathSelection = computed((): Maybe<StoryPlaceHover> => {
    if (tool.value !== "storyPath") return nothing;
    const selected = selection.value;
    if (isNothing(selected)) return nothing;
    const place =
      selected.value.kind === "storyMarker"
        ? findMarkerPlace(doc.value, selected.value.id)
        : selected.value.kind === "landmark"
          ? findLandmarkPlace(doc.value, selected.value.id)
          : nothing;
    return isJust(place) && place.value.sceneFiles.length > 0
      ? just(storyPlaceHover(place.value, arcMoments.value))
      : nothing;
  });

  const storyPathHover = computed((): Maybe<StoryPlaceHover> => {
    if (tool.value !== "storyPath") return nothing;
    const worldM = model.value.cursorWorld;
    if (isNothing(worldM)) return nothing;
    const world = worldM.value;
    const radius = storyPathStopHitRadiusWorld(doc.value.viewport.zoom);
    const markerM = findStoryMarkerAt(doc.value, world, radius);
    if (isJust(markerM)) {
      const placeM = findMarkerPlace(doc.value, markerM.value.id);
      if (isJust(placeM) && placeM.value.sceneFiles.length > 0) {
        return just(storyPlaceHover(placeM.value, arcMoments.value));
      }
    }
    for (const layer of doc.value.layers.toReversed()) {
      if (layer.visible === false) continue;
      for (const lm of layer.landmarks.toReversed()) {
        if (hitTestLandmarkMark(lm, world, doc.value.viewport.zoom) === false) {
          continue;
        }
        const placeM = findLandmarkPlace(doc.value, lm.id);
        // Empty landmarks: no hover card (clutter).
        if (isJust(placeM) && placeM.value.sceneFiles.length > 0) {
          return just(storyPlaceHover(placeM.value, arcMoments.value));
        }
      }
    }
    return nothing;
  });

  function drawImages(
    ctx: CanvasRenderingContext2D,
    layer: MapLayer,
    overlay: boolean,
  ): void {
    for (const image of layer.images) {
      const elM = ensureMapImage(image.path);
      const p = toScreen(image);
      if (overlay === false && isJust(elM)) {
        const size = mapImageDrawSize(image, doc.value.viewport.zoom);
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(((image.rotation ?? 0) * Math.PI) / 180);
        ctx.scale(image.flipX === true ? -1 : 1, image.flipY === true ? -1 : 1);
        ctx.drawImage(
          elM.value,
          -size.width / 2,
          -size.height / 2,
          size.width,
          size.height,
        );
        ctx.restore();
      }
      if (
        overlay &&
        canInteractImages.value &&
        isJust(selection.value) &&
        selection.value.value.kind === "image" &&
        selection.value.value.layerId === layer.id &&
        selection.value.value.id === image.id
      ) {
        const corners = mapImageCorners(image).map(toScreen);
        ctx.save();
        ctx.strokeStyle = "#b47828";
        ctx.lineWidth = 1;
        ctx.beginPath();
        corners.forEach((corner, index) =>
          index === 0
            ? ctx.moveTo(corner.x, corner.y)
            : ctx.lineTo(corner.x, corner.y),
        );
        ctx.closePath();
        ctx.stroke();
        if (
          readonly.value === false &&
          layer.locked === false &&
          canEditLayer.value
        ) {
          const handles = mapImageHandles(image, doc.value.viewport.zoom);
          const top = handles.find((handle) => handle.id === "n")!;
          const rotate = handles.find((handle) => handle.id === "rotate")!;
          const a = toScreen(top.point);
          const b = toScreen(rotate.point);
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
          for (const handle of handles) {
            const point = toScreen(handle.point);
            ctx.fillStyle = "#f7f4ee";
            if (handle.id === "rotate") {
              ctx.beginPath();
              ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
              ctx.fill();
              ctx.stroke();
            } else {
              ctx.fillRect(point.x - 3.5, point.y - 3.5, 7, 7);
              ctx.strokeRect(point.x - 3.5, point.y - 3.5, 7, 7);
            }
          }
        }
        ctx.restore();
      }
    }
  }

  function drawLandmarks(
    ctx: CanvasRenderingContext2D,
    layer: MapLayer,
    overlay: boolean,
  ): void {
    for (const landmark of layer.landmarks) {
      const p = toScreen(landmark);
      if (overlay === false)
        drawLandmarkMark(
          ctx,
          landmark,
          p.x,
          p.y,
          doc.value.viewport.zoom,
          scheduleDraw,
        );
      if (
        overlay &&
        canInteractLandmarks.value &&
        isJust(selection.value) &&
        selection.value.value.kind === "landmark" &&
        selection.value.value.layerId === layer.id &&
        selection.value.value.id === landmark.id
      ) {
        const corners = landmarkCorners(landmark).map(toScreen);
        ctx.save();
        ctx.strokeStyle = "#b47828";
        ctx.lineWidth = 1;
        ctx.beginPath();
        corners.forEach((corner, index) =>
          index === 0
            ? ctx.moveTo(corner.x, corner.y)
            : ctx.lineTo(corner.x, corner.y),
        );
        ctx.closePath();
        ctx.stroke();
        if (
          readonly.value === false &&
          layer.locked === false &&
          canEditLayer.value
        ) {
          const handles = landmarkHandles(landmark, doc.value.viewport.zoom);
          const top = handles.find((handle) => handle.id === "n")!;
          const rotate = handles.find((handle) => handle.id === "rotate")!;
          const a = toScreen(top.point);
          const b = toScreen(rotate.point);
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
          for (const handle of handles) {
            const point = toScreen(handle.point);
            ctx.fillStyle = "#f7f4ee";
            if (handle.id === "rotate") {
              ctx.beginPath();
              ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
              ctx.fill();
              ctx.stroke();
            } else {
              ctx.fillRect(point.x - 3.5, point.y - 3.5, 7, 7);
              ctx.strokeRect(point.x - 3.5, point.y - 3.5, 7, 7);
            }
          }
        }
        ctx.restore();
      }
    }
  }

  function drawLabels(ctx: CanvasRenderingContext2D): void {
    if (isLabelsVisible(doc.value) === false) return;
    for (const label of doc.value.labels) {
      const p = toScreen(label);
      if (
        labelDialogOpen.value &&
        isJust(editingLabel.value) &&
        editingLabel.value.value.id === label.id
      )
        continue;
      drawMapLabel(ctx, label, p, doc.value.viewport.zoom);
      if (
        isJust(selection.value) &&
        selection.value.value.kind === "label" &&
        selection.value.value.id === label.id
      ) {
        const box = labelTransformBox(label);
        ctx.save();
        ctx.strokeStyle = "#b47828";
        ctx.lineWidth = 1;
        ctx.beginPath();
        box.corners.forEach((point, index) =>
          index === 0
            ? ctx.moveTo(point.x, point.y)
            : ctx.lineTo(point.x, point.y),
        );
        ctx.closePath();
        ctx.stroke();
        if (canEditLabels.value) {
          const top = box.handles.find((h) => h.id === "n")!.point;
          const rotation = box.handles.find((h) => h.id === "rotate")!.point;
          ctx.beginPath();
          ctx.moveTo(top.x, top.y);
          ctx.lineTo(rotation.x, rotation.y);
          ctx.stroke();
          ctx.fillStyle = "#f7f4ee";
          for (const handle of box.handles) {
            const point = handle.point;
            if (handle.id === "rotate") {
              ctx.beginPath();
              ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
              ctx.fill();
              ctx.stroke();
            } else {
              ctx.fillRect(point.x - 3.5, point.y - 3.5, 7, 7);
              ctx.strokeRect(point.x - 3.5, point.y - 3.5, 7, 7);
            }
          }
        }
        ctx.restore();
      }
    }
  }

  function drawNodeHandles(ctx: CanvasRenderingContext2D): void {
    const sel = selection.value;
    if (
      readonly.value ||
      isNothing(sel) ||
      (sel.value.kind !== "path" && sel.value.kind !== "node")
    )
      return;
    const path = selectedPath();
    if (isNothing(path)) return;
    const nodeIds = new Set(path.value.nodeIds);
    for (const layer of doc.value.layers) {
      if (
        layer.id !== sel.value.layerId ||
        layer.visible === false ||
        layer.locked
      )
        continue;
      for (const node of layer.nodes) {
        if (nodeIds.has(node.id) === false) continue;
        const p = toScreen(node);
        const selected =
          sel.value.kind === "node" && sel.value.nodeId === node.id;
        ctx.fillStyle = selected ? "#b47828" : "#3d3630";
        ctx.beginPath();
        ctx.arc(p.x, p.y, selected ? 5 : 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  function drawToolPreviews(ctx: CanvasRenderingContext2D): void {
    if (labelCurve.value !== 0 && isJust(labelPreview.value)) {
      drawMapLabel(
        ctx,
        labelPreview.value.value,
        toScreen(labelPreview.value.value),
        doc.value.viewport.zoom,
      );
    }
    if (
      draftPoints.value.length > 0 &&
      isJust(cursorWorld.value) &&
      canEditLayer.value
    ) {
      const layer = activeLayer.value;
      const preview = [...draftPoints.value, cursorWorld.value.value];
      ctx.strokeStyle = inkColor.value;
      ctx.globalAlpha = 0.65;
      ctx.lineWidth = pathLineWidth(inkWeight.value, doc.value.viewport.zoom);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.setLineDash(pathLineDash(inkStyle.value, ctx.lineWidth));
      traceSmoothPath(ctx, preview, toScreen);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      for (const point of draftPoints.value) {
        const p = toScreen(point);
        ctx.fillStyle = "#3d3630";
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
      const join = findNodeNear(
        layer,
        cursorWorld.value.value,
        doc.value.viewport.zoom,
      );
      if (join.tag === "just") {
        const p = toScreen(join.value);
        ctx.strokeStyle = "#b47828";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    if (
      tool.value === "water" &&
      waterMode.value === "river" &&
      readonly.value === false &&
      riverDraftHasPoints(riverDraft.value)
    ) {
      const zoom = doc.value.viewport.zoom;
      const width = Math.max(2, landmassBrushSize.value * zoom);
      const cursorM = cursorWorld.value;
      const forkHighlight = isJust(cursorM)
        ? findRiverForkAttach(
            cursorM.value,
            riverDraft.value.branches,
            riverForkThresholdWorld(),
          )
        : nothing;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (let i = 0; i < riverDraft.value.branches.length; i++) {
        const branch = riverDraft.value.branches[i];
        if (typeof branch === "undefined" || branch.length === 0) continue;
        const isActive = i === riverDraft.value.active;
        const previewPts =
          isActive && isJust(cursorM)
            ? [...branch, cursorM.value]
            : [...branch];
        if (previewPts.length < 2) {
          const alone = toScreen(previewPts[0]!);
          ctx.fillStyle = "rgba(70, 110, 140, 0.7)";
          ctx.beginPath();
          ctx.arc(alone.x, alone.y, width / 2, 0, Math.PI * 2);
          ctx.fill();
          continue;
        }
        ctx.strokeStyle = isActive
          ? "rgba(70, 110, 140, 0.75)"
          : "rgba(70, 110, 140, 0.45)";
        ctx.lineWidth = width;
        // Same Catmull-Rom preview as Line so the cut matches what you see.
        traceSmoothPath(ctx, previewPts, toScreen);
        ctx.stroke();
      }
      if (isJust(forkHighlight)) {
        const p = toScreen(forkHighlight.value.point);
        ctx.strokeStyle = "rgba(70, 110, 140, 0.95)";
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(6, width * 0.35), 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    const stamp = landmarkPreview();
    if (
      isJust(stamp) &&
      isJust(cursorWorld.value) &&
      hoverResize.value === false
    ) {
      const cursor = cursorWorld.value.value;
      const placed = selectedLandmark();
      if (
        isNothing(placed) ||
        placed.value.x !== cursor.x ||
        placed.value.y !== cursor.y
      ) {
        const p = toScreen(cursor);
        ctx.globalAlpha = 0.55;
        drawLandmarkMark(
          ctx,
          stamp.value,
          p.x,
          p.y,
          doc.value.viewport.zoom,
          scheduleDraw,
        );
        ctx.globalAlpha = 1;
      }
    }

    if (
      imagePreviewActive() &&
      isJust(imageStamp.value) &&
      isJust(cursorWorld.value) &&
      hoverResize.value === false
    ) {
      const path = imageStamp.value.value;
      const elM = ensureMapImage(path);
      if (isJust(elM)) {
        const cursor = cursorWorld.value.value;
        const placed = selectedImage();
        if (
          isNothing(placed) ||
          placed.value.x !== cursor.x ||
          placed.value.y !== cursor.y
        ) {
          const width = elM.value.naturalWidth;
          const height = elM.value.naturalHeight;
          if (width > 0 && height > 0) {
            const placeSize =
              stampSize.value !== 1
                ? stampSize.value
                : mapImagePlaceSize(width, height);
            const preview: MapImage = {
              id: "preview",
              x: cursor.x,
              y: cursor.y,
              path,
              width,
              height,
              ...(placeSize === 1 ? {} : { size: placeSize }),
            };
            const size = mapImageDrawSize(preview, doc.value.viewport.zoom);
            const p = toScreen(cursor);
            ctx.save();
            ctx.globalAlpha = 0.55;
            ctx.translate(p.x, p.y);
            ctx.drawImage(
              elM.value,
              -size.width / 2,
              -size.height / 2,
              size.width,
              size.height,
            );
            ctx.restore();
          }
        }
      }
    }

    if (
      (tool.value === "landmass" ||
        tool.value === "terrain" ||
        tool.value === "erase" ||
        (tool.value === "water" && waterMode.value === "lake")) &&
      readonly.value === false &&
      isJust(cursorWorld.value)
    ) {
      const cursor = cursorWorld.value.value;
      const p = toScreen(cursor);
      const radius = Math.max(
        4,
        (landmassBrushSize.value / 2) * doc.value.viewport.zoom,
      );
      const erasing =
        tool.value === "erase" ||
        (landmassSculpting.value === true && activeSculptMode === "erase");
      const cutting =
        (tool.value === "water" && waterMode.value === "lake") ||
        (landmassSculpting.value === true && activeSculptMode === "cut");
      const painting =
        tool.value === "terrain" ||
        (landmassSculpting.value === true && activeSculptMode === "paint");
      ctx.lineWidth = 1.25;
      ctx.setLineDash([4, 3]);
      if (erasing || painting) {
        if (painting && terrainBrushStyle.value !== "feathered") {
          traceJaggedBrushPath(
            ctx,
            p.x,
            p.y,
            radius,
            landmassCoastRoughness01(landmassCoastRoughness.value),
          );
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        }
        // Dual stroke so parchment/light ink stays visible on paper.
        ctx.strokeStyle = "rgba(247, 244, 238, 0.95)";
        ctx.lineWidth = 2.5;
        ctx.stroke();
        ctx.strokeStyle = erasing
          ? "rgba(120, 70, 60, 0.9)"
          : "rgba(42, 36, 28, 0.85)";
        ctx.lineWidth = 1.25;
        ctx.stroke();
        if (painting) {
          ctx.setLineDash([]);
          ctx.globalAlpha = (0.35 * terrainOpacity.value) / 100;
          ctx.fillStyle = fillColor.value;
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      } else if (cutting) {
        ctx.beginPath();
        traceJaggedBrushPath(
          ctx,
          p.x,
          p.y,
          radius,
          landmassCoastRoughness01(landmassCoastRoughness.value),
        );
        ctx.strokeStyle = "rgba(247, 244, 238, 0.95)";
        ctx.lineWidth = 2.5;
        ctx.stroke();
        ctx.strokeStyle = "rgba(70, 110, 140, 0.9)";
        ctx.lineWidth = 1.25;
        ctx.stroke();
      } else {
        ctx.beginPath();
        traceJaggedBrushPath(
          ctx,
          p.x,
          p.y,
          radius,
          landmassCoastRoughness01(landmassCoastRoughness.value),
        );
        ctx.strokeStyle = "rgba(247, 244, 238, 0.95)";
        ctx.lineWidth = 2.5;
        ctx.stroke();
        ctx.strokeStyle = "rgba(90, 110, 70, 0.85)";
        ctx.lineWidth = 1.25;
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }
  }

  function landmarkPreview(): Maybe<MapLandmark> {
    if (
      tool.value !== "landmark" ||
      canEditLayer.value === false ||
      isNothing(landmarkStamp.value)
    )
      return nothing;
    const ink = storedMapInk(inkColor.value);
    return just({
      id: "preview",
      x: 0,
      y: 0,
      ...(stampSize.value === 1 ? {} : { size: stampSize.value }),
      iconId: landmarkStamp.value.value,
      ...(ink.tag === "just" ? { color: ink.value } : {}),
    });
  }

  /** True when the Assets tool has a stamp ready to ghost under the cursor. */
  function imagePreviewActive(): boolean {
    return (
      tool.value === "image" && canEditLayer.value && isJust(imageStamp.value)
    );
  }

  watch(
    [tool, imageStamp],
    () => {
      if (imagePreviewActive() && isJust(imageStamp.value)) {
        ensureMapImage(imageStamp.value.value);
      }
    },
    { flush: "post" },
  );

  function trackCursor(world: WorldPoint): void {
    if (
      draftPoints.value.length === 0 &&
      riverDraftHasPoints(riverDraft.value) === false &&
      isNothing(landmarkPreview()) &&
      imagePreviewActive() === false &&
      tool.value !== "landmass" &&
      tool.value !== "terrain" &&
      tool.value !== "erase" &&
      tool.value !== "water" &&
      tool.value !== "fill"
    ) {
      return;
    }
    void dispatch({ type: "CursorWorldChanged", value: just(world) });
  }

  function pointerScreen(event: MouseEvent): Maybe<ScreenPoint> {
    const canvasM = canvasEl.value;
    if (isNothing(canvasM)) return nothing;
    const canvas = canvasM.value;
    const rect = canvas.getBoundingClientRect();
    return just({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  }

  function finishDraftLine(): void {
    const points = draftPoints.value;
    if (points.length < 2) {
      cancelDraftLine();
      return;
    }
    const layer = activeLayer.value;
    const nodeIds: string[] = [];

    for (const world of points) {
      const existing = findNodeNear(layer, world, doc.value.viewport.zoom);
      const nodeId =
        existing.tag === "just"
          ? existing.value.id
          : (() => {
              const node = { id: newMapId(), x: world.x, y: world.y };
              layer.nodes.push(node);
              return node.id;
            })();
      if (nodeIds[nodeIds.length - 1] !== nodeId) nodeIds.push(nodeId);
    }

    if (nodeIds.length < 2) {
      cancelDraftLine();
      return;
    }

    const path: MapPath = {
      id: newMapId(),
      nodeIds,
      color: inkColor.value,
      weight: inkWeight.value,
      style: inkStyle.value,
    };
    layer.paths.push(path);
    cancelDraftLine();
    void dispatch({
      type: "SelectionChanged",
      value: just({ kind: "path", layerId: layer.id, pathId: path.id }),
    });
  }

  function addDraftPoint(world: WorldPoint): void {
    const layer = activeLayer.value;
    const existing = findNodeNear(layer, world, doc.value.viewport.zoom);
    const point =
      existing.tag === "just"
        ? { x: existing.value.x, y: existing.value.y }
        : { x: world.x, y: world.y };
    const last = atIndexMaybe(draftPoints.value, draftPoints.value.length - 1);
    if (isJust(last) && last.value.x === point.x && last.value.y === point.y)
      return;
    void dispatch({
      type: "DraftPointsChanged",
      value: [...draftPoints.value, point],
    });
  }

  function pickAt(world: WorldPoint, screen: ScreenPoint): Maybe<Selection> {
    // Story path tool: free markers and landmarks only (no path/fill chrome).
    if (tool.value === "storyPath") {
      const radius = storyPathStopHitRadiusWorld(doc.value.viewport.zoom);
      // Prefer numbered path stops so nodes stay grab-able even when the
      // landmark sits on a locked geography layer.
      const stops = buildStoryPath(doc.value, sceneOrder.value);
      for (let i = stops.length - 1; i >= 0; i -= 1) {
        const stop = stops[i];
        if (typeof stop === "undefined") continue;
        if (Math.hypot(world.x - stop.x, world.y - stop.y) > radius) continue;
        if (stop.kind === "marker") {
          return just({ kind: "storyMarker", id: stop.id });
        }
        for (const layer of doc.value.layers) {
          if (layer.landmarks.some((lm) => lm.id === stop.id)) {
            return just({
              kind: "landmark",
              layerId: layer.id,
              id: stop.id,
            });
          }
        }
      }
      const markerM = findStoryMarkerAt(doc.value, world, radius);
      if (isJust(markerM)) {
        return just({ kind: "storyMarker", id: markerM.value.id });
      }
      for (const layer of doc.value.layers.toReversed()) {
        // Locked layers still host story-linked landmarks.
        if (layer.visible === false) continue;
        for (const lm of layer.landmarks.toReversed()) {
          if (hitTestLandmarkMark(lm, world, doc.value.viewport.zoom)) {
            return just({ kind: "landmark", layerId: layer.id, id: lm.id });
          }
        }
      }
      return nothing;
    }
    // Labels sit above Map content in the stack; hit-test them first.
    // Locked labels match locked layers: not selectable or movable.
    // Only select / label tools own label interaction (same as landmarks).
    if (
      (tool.value === "select" || tool.value === "label") &&
      isLabelsVisible(doc.value) === true &&
      isLabelsLocked(doc.value) === false
    ) {
      for (const label of doc.value.labels.toReversed()) {
        if (hitTestLabel(label, world)) {
          return just({ kind: "label", id: label.id });
        }
      }
    }
    for (const layer of doc.value.layers.toReversed()) {
      if (layer.visible === false || layer.locked) continue;
      if (canInteractLandmarks.value) {
        for (const lm of layer.landmarks.toReversed()) {
          if (hitTestLandmarkMark(lm, world, doc.value.viewport.zoom)) {
            return just({ kind: "landmark", layerId: layer.id, id: lm.id });
          }
        }
      }
      if (canInteractImages.value) {
        for (const img of layer.images.toReversed()) {
          if (hitTestMapImage(img, world, doc.value.viewport.zoom)) {
            return just({ kind: "image", layerId: layer.id, id: img.id });
          }
        }
      }
      if (tool.value !== "select" && tool.value !== "line") continue;
      for (const node of layer.nodes) {
        if (hitTestNode(node, world, doc.value.viewport.zoom)) {
          const path = layer.paths.find((p) => p.nodeIds.includes(node.id));
          return just({
            kind: "node",
            layerId: layer.id,
            nodeId: node.id,
            pathId: path?.id ?? "",
          });
        }
      }
      for (const path of layer.paths.toReversed()) {
        if (hitTestPathStroke(layer, path, screen)) {
          return just({ kind: "path", layerId: layer.id, pathId: path.id });
        }
      }
      let bestFill: Maybe<{ id: string; area: number }> = nothing;
      for (const fill of layer.fills) {
        const points = fillPoints(layer, fill);
        if (points.length < 3 || pointInPolygon(world, points) === false)
          continue;
        const area = Math.abs(signedPolygonArea(points));
        if (isNothing(bestFill) || area < bestFill.value.area)
          bestFill = just({ id: fill.id, area });
      }
      if (isJust(bestFill)) {
        return just({ kind: "fill", layerId: layer.id, id: bestFill.value.id });
      }
    }
    return nothing;
  }

  function hitTestPathStroke(
    layer: MapLayer,
    path: MapPath,
    screen: ScreenPoint,
  ): boolean {
    const points = pathPoints(layer, path);
    if (points.length < 2) return false;
    const context = andThenMaybe(canvasEl.value, (canvas) =>
      toMaybe(canvas.getContext("2d")),
    );
    if (isNothing(context)) {
      return hitTestPath(layer, path, toWorld(screen), doc.value.viewport.zoom);
    }
    const ctx = context.value;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    traceSmoothPath(ctx, points, toScreen);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = Math.max(
      16,
      pathLineWidth(path.weight, doc.value.viewport.zoom) + 12,
    );
    const hit = ctx.isPointInStroke(screen.x, screen.y);
    ctx.restore();
    return hit;
  }

  function beginPanAt(screen: ScreenPoint, pointerId: Maybe<number>): void {
    void dispatch({ type: "PanStarted", screen, viewport: doc.value.viewport });
    if (isJust(pointerId)) {
      if (isJust(canvasEl.value))
        canvasEl.value.value.setPointerCapture(pointerId.value);
    }
  }

  function beginPan(event: PointerEvent): void {
    const screen = pointerScreen(event);
    if (isNothing(screen)) return;
    beginPanAt(screen.value, just(event.pointerId));
  }

  /**
   * After a Story path press moves past the drag threshold: relocate every
   * scene on that stop. Empty free markers still move in place.
   */
  function resolveStoryPathPendingDrag(
    hit: Extract<Selection, { kind: "landmark" | "storyMarker" }>,
    pendingScreen: ScreenPoint,
    event: PointerEvent,
  ): void {
    if (readonly.value === false) {
      const placeM =
        hit.kind === "landmark"
          ? findLandmarkPlace(doc.value, hit.id)
          : findMarkerPlace(doc.value, hit.id);
      const paths =
        isJust(placeM) && placeM.value.sceneFiles.length > 0
          ? placeM.value.sceneFiles
          : [];
      const startNodeDrag = options.onStoryNodeDragStart;
      if (paths.length > 0 && typeof startNodeDrag === "function") {
        event.preventDefault();
        // Hand the gesture to the window-level story drag listeners.
        if (isJust(canvasEl.value) && event.pointerId >= 0) {
          try {
            canvasEl.value.value.releasePointerCapture(event.pointerId);
          } catch {
            /* ignore */
          }
        }
        startNodeDrag({
          paths,
          from: {
            kind: hit.kind === "landmark" ? "landmark" : "marker",
            id: hit.id,
          },
          event,
        });
        return;
      }
      if (hit.kind === "storyMarker") {
        pushUndo();
        void dispatch({
          type: "DraggingChanged",
          value: just({ kind: "storyMarker", id: hit.id }),
        });
        return;
      }
    }
    beginPanAt(pendingScreen, nothing);
  }

  function deleteStoryPathPlace(
    hit: Extract<Selection, { kind: "landmark" | "storyMarker" }>,
  ): void {
    if (readonly.value) return;
    if (hit.kind === "storyMarker") {
      pushUndo();
      doc.value.storyMarkers = doc.value.storyMarkers.filter(
        (m) => m.id !== hit.id,
      );
      storyPlaceFocusKey = nothing;
      void dispatch({ type: "SelectionChanged", value: nothing });
      scheduleDraw();
      return;
    }
    // Landmark art stays; clearing scene links removes it from the Story path.
    for (const layer of doc.value.layers) {
      const landmark = toMaybe(layer.landmarks.find((lm) => lm.id === hit.id));
      if (isNothing(landmark)) continue;
      pushUndo();
      delete landmark.value.sceneLinks;
      storyPlaceFocusKey = nothing;
      void dispatch({ type: "SelectionChanged", value: nothing });
      scheduleDraw();
      return;
    }
  }

  function onContextMenu(event: MouseEvent): void {
    if (tool.value !== "storyPath" || readonly.value) return;
    const screenM = pointerScreen(event);
    if (isNothing(screenM)) return;
    const world = toWorld(screenM.value);
    const picked = pickAt(world, screenM.value);
    if (isNothing(picked)) return;
    const hit = picked.value;
    if (hit.kind !== "landmark" && hit.kind !== "storyMarker") return;
    void openContextMenu(event, [
      {
        id: "story-path-delete-node",
        label: "Delete",
        action: () => {
          deleteStoryPathPlace(hit);
        },
      },
    ]);
  }

  function selectLandmarkAt(layerId: string, id: string): void {
    void dispatch({
      type: "SelectionChanged",
      value: just({ kind: "landmark", layerId, id }),
    });
    const lmM = landmarkById(layerId, id);
    if (isNothing(lmM)) return;
    const lm = lmM.value;
    void dispatch({ type: "StampSizeChanged", value: landmarkScale(lm) });
    void dispatch({ type: "InkColorChanged", value: landmarkInk(lm) });
    const iconId = toMaybe(lm.iconId);
    if (isJust(iconId) && iconId.value.length > 0)
      void dispatch({ type: "LandmarkStampChanged", value: iconId });
  }

  function selectImageAt(layerId: string, id: string): void {
    void dispatch({
      type: "SelectionChanged",
      value: just({ kind: "image", layerId, id }),
    });
    const imgM = imageById(layerId, id);
    if (isNothing(imgM)) return;
    void dispatch({
      type: "StampSizeChanged",
      value: mapImageScale(imgM.value),
    });
    void dispatch({
      type: "ImageStampChanged",
      value: just(imgM.value.path),
    });
  }

  function selectPathAt(layerId: string, pathId: string): void {
    void dispatch({
      type: "SelectionChanged",
      value: just({ kind: "path", layerId, pathId }),
    });
    const pathM = selectedPath();
    if (isNothing(pathM)) return;
    const path = pathM.value;
    void dispatch({ type: "InkColorChanged", value: path.color });
    void dispatch({ type: "InkWeightChanged", value: path.weight });
    void dispatch({ type: "InkStyleChanged", value: path.style ?? "solid" });
  }

  function selectNodeAt(layerId: string, nodeId: string, pathId: string): void {
    void dispatch({
      type: "SelectionChanged",
      value: just({ kind: "node", layerId, nodeId, pathId }),
    });
    const pathM = selectedPath();
    if (isNothing(pathM)) return;
    const path = pathM.value;
    void dispatch({ type: "InkColorChanged", value: path.color });
    void dispatch({ type: "InkWeightChanged", value: path.weight });
    void dispatch({ type: "InkStyleChanged", value: path.style ?? "solid" });
  }

  function commitPendingSelect(
    hit: Extract<
      Selection,
      { kind: "landmark" | "image" | "storyMarker" | "path" | "node" }
    >,
  ): void {
    if (hit.kind === "landmark") {
      selectLandmarkAt(hit.layerId, hit.id);
      return;
    }
    if (hit.kind === "image") {
      selectImageAt(hit.layerId, hit.id);
      return;
    }
    if (hit.kind === "storyMarker") {
      void dispatch({
        type: "SelectionChanged",
        value: just({ kind: "storyMarker", id: hit.id }),
      });
      return;
    }
    if (hit.kind === "path") {
      selectPathAt(hit.layerId, hit.pathId);
      return;
    }
    selectNodeAt(hit.layerId, hit.nodeId, hit.pathId);
  }

  function hitAlreadySelected(
    hit: Extract<Selection, { kind: "landmark" | "image" | "path" | "node" }>,
  ): boolean {
    const selM = selection.value;
    if (isNothing(selM)) return false;
    const sel = selM.value;
    if (hit.kind === "landmark") {
      return (
        sel.kind === "landmark" &&
        sel.layerId === hit.layerId &&
        sel.id === hit.id
      );
    }
    if (hit.kind === "image") {
      return (
        sel.kind === "image" && sel.layerId === hit.layerId && sel.id === hit.id
      );
    }
    if (hit.kind === "path") {
      return (
        (sel.kind === "path" &&
          sel.layerId === hit.layerId &&
          sel.pathId === hit.pathId) ||
        (sel.kind === "node" &&
          sel.layerId === hit.layerId &&
          sel.pathId === hit.pathId)
      );
    }
    // Node: movable when this node or its parent path is already selected.
    return (
      (sel.kind === "node" &&
        sel.layerId === hit.layerId &&
        (sel.nodeId === hit.nodeId || sel.pathId === hit.pathId)) ||
      (sel.kind === "path" &&
        sel.layerId === hit.layerId &&
        sel.pathId === hit.pathId)
    );
  }

  function onPointerDown(event: PointerEvent): void {
    if (labelDialogOpen.value && event.button === 0) {
      commitLabel();
      return;
    }
    if (event.button === 1 || spaceHeld.value) {
      beginPan(event);
      return;
    }
    if (event.button !== 0) return;

    // View-only: left-drag pans (same as Space / middle-click).
    if (readonly.value) {
      beginPan(event);
      return;
    }

    const screenM = pointerScreen(event);
    if (isNothing(screenM)) return;
    const screen = screenM.value;
    const world = toWorld(screen);
    if (tool.value === "crop") {
      const handle = hitCropHandle(screen);
      const draft = model.value.cropDraft;
      if (isJust(handle) && isJust(draft)) {
        void dispatch({
          type: "CropResizeStarted",
          handle: handle.value,
          start: {
            x: Math.min(draft.value.start.x, draft.value.end.x),
            y: Math.min(draft.value.start.y, draft.value.end.y),
          },
          end: {
            x: Math.max(draft.value.start.x, draft.value.end.x),
            y: Math.max(draft.value.start.y, draft.value.end.y),
          },
        });
        if (isJust(canvasEl.value))
          canvasEl.value.value.setPointerCapture(event.pointerId);
        return;
      }
      if (pointOnMap(doc.value, world)) {
        void dispatch({
          type: "CropDraftChanged",
          value: just({ start: world, end: world }),
          drawing: true,
        });
        if (isJust(canvasEl.value))
          canvasEl.value.value.setPointerCapture(event.pointerId);
      }
      return;
    }
    if (beginHandleResize(screen)) {
      if (isJust(canvasEl.value))
        canvasEl.value.value.setPointerCapture(event.pointerId);
      return;
    }
    if (pointOnMap(doc.value, world) === false) {
      if (tool.value === "select") beginPan(event);
      return;
    }
    if (tool.value === "landmass" && readonly.value === false) {
      beginLandmassStroke("add", world, event.pointerId);
      return;
    }
    if (tool.value === "terrain" && readonly.value === false) {
      beginLandmassStroke("paint", world, event.pointerId);
      return;
    }
    if (tool.value === "water" && readonly.value === false) {
      if (waterMode.value === "lake") {
        beginLandmassStroke("cut", world, event.pointerId);
        return;
      }
      addRiverDraftPoint(world);
      void dispatch({ type: "CursorWorldChanged", value: just(world) });
      if (isJust(canvasEl.value))
        canvasEl.value.value.setPointerCapture(event.pointerId);
      scheduleDraw();
      return;
    }
    if (tool.value === "line" && canEditLayer.value) {
      if (draftPoints.value.length === 0) pushUndo();
      addDraftPoint(world);
      void dispatch({ type: "CursorWorldChanged", value: just(world) });
      if (isJust(canvasEl.value))
        canvasEl.value.value.setPointerCapture(event.pointerId);
      return;
    }
    if (tool.value === "landmark" && canEditLayer.value) {
      if (isNothing(landmarkStamp.value)) return;
      pushUndo();
      const layer = activeLayer.value;
      const ink = storedMapInk(inkColor.value);
      const landmark: MapLandmark = {
        id: newMapId(),
        x: world.x,
        y: world.y,
        ...(stampSize.value === 1 ? {} : { size: stampSize.value }),
        iconId: landmarkStamp.value.value,
        ...(isJust(ink) ? { color: ink.value } : {}),
      };
      layer.landmarks.push(landmark);
      void dispatch({
        type: "SelectionChanged",
        value: just({
          kind: "landmark",
          layerId: layer.id,
          id: landmark.id,
        }),
      });
      scheduleDraw();
      return;
    }
    if (tool.value === "image" && canEditLayer.value) {
      if (isNothing(imageStamp.value)) return;
      const path = imageStamp.value.value;
      const placeAt = world;
      void loadMapImage(path).then((elM) => {
        if (isNothing(elM)) return;
        const width = elM.value.naturalWidth;
        const height = elM.value.naturalHeight;
        if (width <= 0 || height <= 0) return;
        pushUndo();
        const layer = activeLayer.value;
        const placeSize =
          stampSize.value !== 1
            ? stampSize.value
            : mapImagePlaceSize(width, height);
        const image: MapImage = {
          id: newMapId(),
          x: placeAt.x,
          y: placeAt.y,
          path,
          width,
          height,
          ...(placeSize === 1 ? {} : { size: placeSize }),
        };
        layer.images.push(image);
        void dispatch({
          type: "SelectionChanged",
          value: just({
            kind: "image",
            layerId: layer.id,
            id: image.id,
          }),
        });
        void dispatch({
          type: "StampSizeChanged",
          value: mapImageScale(image),
        });
        scheduleDraw();
      });
      return;
    }
    if (tool.value === "fill" && canEditLayer.value) {
      applyFillAt(world);
      return;
    }
    if (tool.value === "label" && canEditLabels.value) {
      event.preventDefault();
      const existing = pickAt(world, screen);
      if (isJust(existing) && existing.value.kind === "label") {
        beginEditLabel(existing.value.id);
        return;
      }
      void dispatch({
        type: "LabelPlacementStarted",
        point: world,
      });
      return;
    }
    if (tool.value === "erase" && readonly.value === false) {
      beginEraseStroke(world, event.pointerId);
      return;
    }
    if (tool.value === "storyPath") {
      const pickedStory = pickAt(world, screen);
      void dispatch({ type: "CursorWorldChanged", value: just(world) });
      if (isNothing(pickedStory)) {
        storyPlaceFocusKey = nothing;
        void dispatch({ type: "SelectionChanged", value: nothing });
        beginPan(event);
        scheduleDraw();
        return;
      }
      const hit = pickedStory.value;
      if (hit.kind !== "landmark" && hit.kind !== "storyMarker") {
        storyPlaceFocusKey = nothing;
        void dispatch({ type: "SelectionChanged", value: nothing });
        beginPan(event);
        scheduleDraw();
        return;
      }
      // Focus the path stop. Drag past threshold relocates its scenes.
      storyPlaceFocusKey = just(
        storyPathPlaceKey(
          hit.kind === "landmark" ? "landmark" : "marker",
          hit.id,
        ),
      );
      void dispatch({
        type: "SelectionChanged",
        value: pickedStory,
      });
      void dispatch({
        type: "PendingSelectClickChanged",
        value: just({ hit, screen, wasSelected: true }),
      });
      if (isJust(canvasEl.value)) {
        canvasEl.value.value.setPointerCapture(event.pointerId);
      }
      scheduleDraw();
      return;
    }

    const picked = pickAt(world, screen);
    if (isNothing(picked)) {
      void dispatch({ type: "SelectionChanged", value: nothing });
      beginPan(event);
      scheduleDraw();
      return;
    }
    const hit = picked.value;
    // Landmark / path / node: select on pointerup. Drag pans unless already
    // selected (then landmark/node can move).
    const deferSelect =
      hit.kind === "landmark" ||
      hit.kind === "image" ||
      hit.kind === "path" ||
      hit.kind === "node";
    if (deferSelect === false) {
      void dispatch({ type: "SelectionChanged", value: picked });
    }
    if (hit.kind === "fill") {
      const fill = selectedFill();
      if (isJust(fill)) {
        void dispatch({ type: "FillColorChanged", value: fill.value.color });
      }
    }
    if (hit.kind === "label") {
      const label = labelById(hit.id);
      if (isJust(label)) {
        void dispatch({
          type: "LabelFontChanged",
          value: label.value.font ?? "cartographic",
        });
        void dispatch({
          type: "LabelCurveChanged",
          value: label.value.curve ?? 0,
        });
        void dispatch({
          type: "LabelSpacingChanged",
          value: label.value.letterSpacing ?? 0,
        });
        void dispatch({
          type: "LabelRotationChanged",
          value: label.value.rotation ?? 0,
        });
        void dispatch({
          type: "InkColorChanged",
          value: labelInk(label.value),
        });
        void dispatch({
          type: "LabelSizeChanged",
          value: labelScale(label.value),
        });
      }
    }
    if (
      hit.kind === "landmark" ||
      hit.kind === "image" ||
      hit.kind === "path" ||
      hit.kind === "node"
    ) {
      void dispatch({
        type: "PendingSelectClickChanged",
        value: just({ hit, screen, wasSelected: hitAlreadySelected(hit) }),
      });
      if (isJust(canvasEl.value))
        canvasEl.value.value.setPointerCapture(event.pointerId);
    } else if (hit.kind === "label") {
      void dispatch({
        type: "PendingLabelClickChanged",
        value: just({ id: hit.id, screen }),
      });
      if (isJust(canvasEl.value))
        canvasEl.value.value.setPointerCapture(event.pointerId);
    }
    // Selection lives outside the document; redraw so chrome matches the
    // pick even when the pointer never moves (no drag frame).
    scheduleDraw();
  }

  function screenDist(a: ScreenPoint, b: ScreenPoint): number {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function beginLandmarkResize(
    landmark: MapLandmark,
    screen: ScreenPoint,
    handle: LandmarkHandle,
  ): void {
    const sel = selection.value;
    const layerId =
      isJust(sel) && sel.value.kind === "landmark"
        ? sel.value.layerId
        : doc.value.activeLayerId;
    pushUndo();
    void dispatch({
      type: "DraggingChanged",
      value: just({
        kind: "resize-landmark",
        layerId,
        id: landmark.id,
        start: { ...landmark },
        handle,
        pointerStart: toWorld(screen),
      }),
    });
  }

  function beginImageResize(
    image: MapImage,
    screen: ScreenPoint,
    handle: LandmarkHandle,
  ): void {
    const sel = selection.value;
    const layerId =
      isJust(sel) && sel.value.kind === "image"
        ? sel.value.layerId
        : doc.value.activeLayerId;
    pushUndo();
    void dispatch({
      type: "DraggingChanged",
      value: just({
        kind: "resize-image",
        layerId,
        id: image.id,
        start: { ...image },
        handle,
        pointerStart: toWorld(screen),
      }),
    });
  }

  function beginLabelResize(
    label: MapLabel,
    screen: ScreenPoint,
    handle: LandmarkHandle,
  ): void {
    const box = labelTransformBox(label);
    pushUndo();
    void dispatch({
      type: "DraggingChanged",
      value: just({
        kind: "resize-label",
        id: label.id,
        start: { ...label },
        handle,
        pointerStart: toWorld(screen),
        dimensions: {
          width: box.width / doc.value.viewport.zoom,
          height: box.height / doc.value.viewport.zoom,
        },
      }),
    });
  }

  function stampNativeLongestSide(): number {
    const imgM = selectedImage();
    if (isJust(imgM)) return Math.max(imgM.value.width, imgM.value.height, 1);
    const lmM = selectedLandmark();
    if (isJust(lmM)) {
      const native = landmarkNativeSize(lmM.value);
      return Math.max(native.width, native.height, 1);
    }
    if (tool.value === "image" && isJust(imageStamp.value)) {
      const elM = getLoadedMapImage(imageStamp.value.value);
      if (isJust(elM))
        return Math.max(elM.value.naturalWidth, elM.value.naturalHeight, 1);
    }
    if (tool.value === "landmark" && isJust(landmarkStamp.value)) {
      const native = landmarkNativeSize({
        id: "stamp",
        x: 0,
        y: 0,
        iconId: landmarkStamp.value.value,
      });
      return Math.max(native.width, native.height, 1);
    }
    return 1 / LANDMARK_SIZE_MIN;
  }

  const objectSizeMin = computed(() =>
    mapObjectSizeMin(stampNativeLongestSide()),
  );

  function applyStampSize(): void {
    const clamped = clampMapObjectSize(
      stampSize.value,
      stampNativeLongestSide(),
    );
    void dispatch({
      type: "StampSizeChanged",
      value: clamped,
    });
    const imgM = selectedImage();
    if (isJust(imgM)) {
      const img = imgM.value;
      if (clamped === 1) delete img.size;
      else img.size = clamped;
      scheduleDraw();
      return;
    }
    const lmM = selectedLandmark();
    if (isNothing(lmM)) return;
    const lm = lmM.value;
    if (clamped === 1) delete lm.size;
    else lm.size = clamped;
  }

  function applyInkColor(): void {
    if (labelDialogOpen.value) return;
    const lm = selectedLandmark();
    if (isJust(lm)) {
      const ink = storedMapInk(inkColor.value);
      if (isNothing(ink)) delete lm.value.color;
      else lm.value.color = ink.value;
      scheduleDraw();
      return;
    }
    const label = selectedLabel();
    if (isJust(label)) {
      const ink = storedMapInk(inkColor.value);
      if (isNothing(ink)) delete label.value.color;
      else label.value.color = ink.value;
      scheduleDraw();
      return;
    }
    const path = selectedPath();
    if (isJust(path)) {
      path.value.color = inkColor.value;
      scheduleDraw();
      return;
    }
  }

  function applyFillColor(): void {
    const fill = selectedFill();
    if (isJust(fill)) {
      fill.value.color = fillColor.value;
      scheduleDraw();
    }
  }

  function applyInkStyle(): void {
    const path = selectedPath();
    if (isNothing(path) || (path.value.style ?? "solid") === inkStyle.value)
      return;
    pushUndo();
    path.value.style = inkStyle.value;
    scheduleDraw();
  }

  function applyInkWeight(): void {
    const path = selectedPath();
    if (isNothing(path) || path.value.weight === inkWeight.value) return;
    pushUndo();
    path.value.weight = inkWeight.value;
    scheduleDraw();
  }

  function applyLabelAppearance(recordUndo: boolean): void {
    if (labelDialogOpen.value) return;
    const label = selectedLabel();
    if (isNothing(label)) return;
    if (
      (label.value.font ?? "cartographic") === labelFont.value &&
      (label.value.rotation ?? 0) === labelRotation.value &&
      (label.value.curve ?? 0) === labelCurve.value &&
      (label.value.letterSpacing ?? 0) === labelSpacing.value
    )
      return;
    if (recordUndo) pushUndo();
    label.value.font = labelFont.value;
    label.value.rotation = labelRotation.value;
    label.value.curve = labelCurve.value;
    label.value.letterSpacing = labelSpacing.value;
    scheduleDraw();
  }

  function applyLabelSize(): void {
    void dispatch({
      type: "LabelSizeChanged",
      value: clampLabelSize(labelSize.value),
    });
    if (labelDialogOpen.value) return;
    const labelM = selectedLabel();
    if (isNothing(labelM)) return;
    const label = labelM.value;
    if (labelSize.value === 1) delete label.size;
    else label.size = labelSize.value;
    scheduleDraw();
  }

  function onPointerMove(event: PointerEvent): void {
    const screenM = pointerScreen(event);
    if (isNothing(screenM)) return;
    const screen = screenM.value;
    if (model.value.cropDrawing && isJust(model.value.cropDraft)) {
      const draft = model.value.cropDraft.value;
      const page = mapPage(doc.value);
      const pointer = clampPointToMapPage(page, toWorld(screen));
      const handle = model.value.cropHandle;
      if (isJust(handle)) {
        const direction = LANDMARK_HANDLES.find((h) => h.id === handle.value)!;
        const start = { ...draft.start },
          end = { ...draft.end };
        if (direction.x < 0) start.x = Math.min(pointer.x, end.x - 64);
        if (direction.x > 0) end.x = Math.max(pointer.x, start.x + 64);
        if (direction.y < 0) start.y = Math.min(pointer.y, end.y - 64);
        if (direction.y > 0) end.y = Math.max(pointer.y, start.y + 64);
        void dispatch({
          type: "CropDraftChanged",
          value: just({
            start: clampPointToMapPage(page, start),
            end: clampPointToMapPage(page, end),
          }),
          drawing: true,
        });
      } else {
        void dispatch({
          type: "CropDraftChanged",
          value: just({ start: draft.start, end: pointer }),
          drawing: true,
        });
      }
      return;
    }
    if (isJust(pendingSelectClick.value)) {
      const pending = pendingSelectClick.value.value;
      if (screenDist(screen, pending.screen) > 5) {
        void dispatch({
          type: "PendingSelectClickChanged",
          value: nothing,
        });
        if (
          tool.value === "storyPath" &&
          (pending.hit.kind === "landmark" ||
            pending.hit.kind === "storyMarker")
        ) {
          resolveStoryPathPendingDrag(pending.hit, pending.screen, event);
          return;
        }
        const canMove =
          pending.wasSelected === true &&
          readonly.value === false &&
          (pending.hit.kind === "landmark" ||
            pending.hit.kind === "image" ||
            pending.hit.kind === "storyMarker" ||
            pending.hit.kind === "node");
        if (canMove && pending.hit.kind === "landmark") {
          pushUndo();
          void dispatch({
            type: "DraggingChanged",
            value: just({
              kind: "landmark",
              layerId: pending.hit.layerId,
              id: pending.hit.id,
            }),
          });
        } else if (canMove && pending.hit.kind === "image") {
          pushUndo();
          void dispatch({
            type: "DraggingChanged",
            value: just({
              kind: "image",
              layerId: pending.hit.layerId,
              id: pending.hit.id,
            }),
          });
        } else if (canMove && pending.hit.kind === "storyMarker") {
          pushUndo();
          void dispatch({
            type: "DraggingChanged",
            value: just({ kind: "storyMarker", id: pending.hit.id }),
          });
        } else if (canMove && pending.hit.kind === "node") {
          pushUndo();
          void dispatch({
            type: "DraggingChanged",
            value: just(pending.hit),
          });
        } else {
          // Unselected (or path): drag pans instead of selecting.
          beginPanAt(pending.screen, nothing);
        }
      }
    }
    if (panning.value && isJust(panStart.value)) {
      const dx = screen.x - panStart.value.value.screen.x;
      const dy = screen.y - panStart.value.value.screen.y;
      doc.value.viewport.x =
        panStart.value.value.viewport.x - dx / doc.value.viewport.zoom;
      doc.value.viewport.y =
        panStart.value.value.viewport.y - dy / doc.value.viewport.zoom;
      void dispatch({ type: "HoverResizeChanged", value: false });
      const world = toWorld(screen);
      trackCursor(world);
      updateFillHover(world);
      scheduleDraw();
      return;
    }
    if (landmassSculpting.value === true && readonly.value === false) {
      const worldRawMove = toWorld(screen);
      if (pointOnMap(doc.value, worldRawMove) === false) {
        lastLandmassPaint = nothing;
        return;
      }
      if (activeSculptMode === "erase") {
        if (
          typeof doc.value.landmass !== "undefined" ||
          isJust(landmassCanvas)
        ) {
          paintLandmassAt(worldRawMove);
        }
        eraseObjectsUnderBrush(worldRawMove);
      } else {
        paintLandmassAt(worldRawMove);
      }
      void dispatch({ type: "CursorWorldChanged", value: just(worldRawMove) });
      scheduleDraw();
      return;
    }
    void dispatch({
      type: "HoverResizeChanged",
      value: overResizeHandle(screen),
    });
    const world = toWorld(screen);
    if (
      tool.value === "select" ||
      tool.value === "crop" ||
      tool.value === "storyPath"
    ) {
      void dispatch({ type: "CursorWorldChanged", value: just(world) });
      if (tool.value === "storyPath") scheduleDraw();
    }
    updateFillHover(world);
    if (
      draftPoints.value.length > 0 ||
      riverDraftHasPoints(riverDraft.value) ||
      isJust(landmarkPreview()) ||
      imagePreviewActive() ||
      tool.value === "fill" ||
      tool.value === "landmass" ||
      tool.value === "terrain" ||
      tool.value === "erase" ||
      tool.value === "water"
    ) {
      trackCursor(world);
      scheduleDraw();
    }
    if (isJust(pendingLabelClick.value) && canEditLabels.value) {
      const pending = pendingLabelClick.value.value;
      if (
        Math.hypot(screen.x - pending.screen.x, screen.y - pending.screen.y) > 5
      ) {
        pushUndo();
        void dispatch({
          type: "DraggingChanged",
          value: just({
            kind: "label",
            id: pending.id,
          }),
        });
        void dispatch({
          type: "PendingLabelClickChanged",
          value: nothing,
        });
      }
    }
    if (isNothing(dragging.value) || readonly.value) return;
    const drag = dragging.value.value;
    if (drag.kind === "label") {
      const found = labelById(drag.id);
      if (isJust(found)) {
        found.value.x = world.x;
        found.value.y = world.y;
      }
      scheduleDraw();
      return;
    }
    if (drag.kind === "resize-label") {
      const found = labelById(drag.id);
      if (isJust(found)) {
        const next = transformLabel(
          drag.start,
          drag.handle,
          drag.pointerStart,
          world,
          drag.dimensions,
          event.shiftKey,
        );
        Object.assign(found.value, next);
        void dispatch({ type: "LabelSizeChanged", value: labelScale(next) });
        void dispatch({
          type: "LabelRotationChanged",
          value: next.rotation ?? 0,
        });
      }
      scheduleDraw();
      return;
    }
    if (drag.kind === "storyMarker") {
      const found = toMaybe(
        doc.value.storyMarkers.find((m) => m.id === drag.id),
      );
      if (isJust(found)) {
        found.value.x = world.x;
        found.value.y = world.y;
      }
      scheduleDraw();
      return;
    }
    const layerM = layerById(drag.layerId);
    if (isNothing(layerM)) return;
    const layer = layerM.value;
    if (drag.kind === "node") {
      const node = nodeById(layer, drag.nodeId);
      if (isJust(node)) {
        node.value.x = world.x;
        node.value.y = world.y;
      }
    } else if (drag.kind === "landmark") {
      const found = toMaybe(layer.landmarks.find((l) => l.id === drag.id));
      if (isJust(found)) {
        const lm = found.value;
        lm.x = world.x;
        lm.y = world.y;
      }
    } else if (drag.kind === "image") {
      const found = toMaybe(layer.images.find((img) => img.id === drag.id));
      if (isJust(found)) {
        found.value.x = world.x;
        found.value.y = world.y;
      }
    } else if (drag.kind === "resize-landmark") {
      const found = toMaybe(layer.landmarks.find((l) => l.id === drag.id));
      if (isJust(found)) {
        Object.assign(
          found.value,
          transformLandmark(
            drag.start,
            drag.handle,
            world,
            drag.pointerStart,
            event.shiftKey,
          ),
        );
      }
    } else if (drag.kind === "resize-image") {
      const found = toMaybe(layer.images.find((img) => img.id === drag.id));
      if (isJust(found)) {
        Object.assign(
          found.value,
          transformMapImage(
            drag.start,
            drag.handle,
            world,
            drag.pointerStart,
            event.shiftKey,
          ),
        );
        void dispatch({
          type: "StampSizeChanged",
          value: mapImageScale(found.value),
        });
      }
    }
    scheduleDraw();
  }

  function onPointerUp(event: PointerEvent): void {
    if (model.value.cropDrawing) {
      void dispatch({
        type: "CropDraftChanged",
        value: model.value.cropDraft,
        drawing: false,
      });
    }
    const wasPanning = panning.value === true;
    const wasSculpting = landmassSculpting.value === true;
    const pendingSelect = pendingSelectClick.value;
    void dispatch({ type: "PointerReleased" });
    lastLandmassPaint = nothing;
    try {
      if (isJust(canvasEl.value))
        canvasEl.value.value.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
    if (wasSculpting) {
      invalidateCoasts();
      ensureCoasts();
      options.onLandmassDirty?.();
      scheduleDraw();
    }
    // Click (no drag): select. Pan-from-hit skips this.
    if (isJust(pendingSelect) && wasPanning === false) {
      commitPendingSelect(pendingSelect.value.hit);
      scheduleDraw();
    }
  }

  function onPointerLeave(): void {
    void dispatch({ type: "PointerLeft" });
  }

  /** Keep `focusWorld` under `focusScreen` after changing zoom (never grid-snap). */
  function applyZoomAroundFocus(
    zoom: number,
    focusScreen: ScreenPoint,
    focusWorld: WorldPoint,
  ): void {
    doc.value.viewport.zoom = zoom;
    const after = toWorld(focusScreen);
    doc.value.viewport.x += focusWorld.x - after.x;
    doc.value.viewport.y += focusWorld.y - after.y;
  }

  function tickZoomSmooth(): void {
    zoomRaf = nothing;
    if (isNothing(zoomTarget)) return;
    const z = doc.value.viewport.zoom;
    const target = zoomTarget.value;
    const delta = target - z;
    if (Math.abs(delta) < 0.0006) {
      applyZoomAroundFocus(target, zoomFocusScreen, zoomFocusWorld);
      zoomTarget = nothing;
      scheduleDraw();
      return;
    }
    // Ease toward the target so trackpad / wheel bursts don't stutter.
    applyZoomAroundFocus(z + delta * 0.32, zoomFocusScreen, zoomFocusWorld);
    scheduleDraw();
    zoomRaf = just(requestAnimationFrame(() => tickZoomSmooth()));
  }

  function onWheel(event: WheelEvent): void {
    event.preventDefault();
    const canvasM = canvasEl.value;
    if (isNothing(canvasM)) return;
    const canvas = canvasM.value;
    const rect = canvas.getBoundingClientRect();
    const screen = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    // Pixel-normalized delta (LINE / PAGE → approx CSS pixels).
    const raw =
      event.deltaMode === 1
        ? event.deltaY * 16
        : event.deltaMode === 2
          ? event.deltaY * rect.height
          : event.deltaY;
    // Compress big fling spikes; keep small trackpad steps almost 1:1.
    const compressed = 64 * Math.tanh(raw / 64);
    const factor = Math.exp(-compressed * 0.0014);
    const base = isJust(zoomTarget)
      ? zoomTarget.value
      : doc.value.viewport.zoom;
    const next = Math.max(MAP_ZOOM_MIN, Math.min(MAP_ZOOM_MAX, base * factor));
    if (next === base && isJust(zoomTarget)) return;

    // Anchor on the unsnapped world point under the cursor. Snapping here
    // (via toWorld) used to make zoom jump between grid cells.
    zoomFocusScreen = screen;
    zoomFocusWorld = toWorld(screen);
    zoomTarget = just(next);
    if (isNothing(zoomRaf)) {
      zoomRaf = just(requestAnimationFrame(() => tickZoomSmooth()));
    }
  }

  function isTypingInField(event: Event): boolean {
    const target = event.target;
    if (target instanceof HTMLElement === false) return false;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement
    ) {
      return true;
    }
    return target.isContentEditable;
  }

  function panByScreen(dx: number, dy: number): void {
    const zoom = doc.value.viewport.zoom;
    doc.value.viewport.x += dx / zoom;
    doc.value.viewport.y += dy / zoom;
    scheduleDraw();
  }

  function isShortcutHelpKey(event: KeyboardEvent): boolean {
    if (event.key === "?") return true;
    return event.key === "/" && (event.metaKey || event.ctrlKey);
  }

  function closeShortcuts(): void {
    void dispatch({ type: "ShortcutsClosed" });
  }

  function toggleShortcuts(): void {
    void dispatch({ type: "ShortcutsToggled" });
  }

  function onKeyDown(event: KeyboardEvent): void {
    if (event.defaultPrevented || isTypingInField(event)) return;
    if (labelDialogOpen.value && event.key === "Escape") {
      event.preventDefault();
      cancelLabelDialog();
      return;
    }
    if (isShortcutHelpKey(event)) {
      event.preventDefault();
      toggleShortcuts();
      return;
    }
    if (shortcutsOpen.value || labelDialogOpen.value) return;
    if (event.code === "Space")
      void dispatch({ type: "SpaceHeldChanged", value: true });
    if (
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight" ||
      event.key === "ArrowUp" ||
      event.key === "ArrowDown"
    ) {
      event.preventDefault();
      const step = event.shiftKey ? 168 : 56;
      if (event.key === "ArrowLeft") panByScreen(-step, 0);
      else if (event.key === "ArrowRight") panByScreen(step, 0);
      else if (event.key === "ArrowUp") panByScreen(0, -step);
      else panByScreen(0, step);
      return;
    }
    if (readonly.value) return;
    if ((event.metaKey || event.ctrlKey) && event.key === "z") {
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
      return;
    }
    if (event.key === "Escape") {
      // Open menus own Escape (close the menu only; keep landmark selection).
      if (document.querySelector('.zw-menu [aria-expanded="true"]') !== null) {
        return;
      }
      event.preventDefault();
      storyPlaceFocusKey = nothing;
      void dispatch({ type: "EscapePressed" });
      return;
    }
    if (event.key === "Enter" && tool.value === "crop") {
      event.preventDefault();
      applyCrop();
      return;
    }
    if (event.key === "Enter" && draftPoints.value.length > 0) {
      event.preventDefault();
      finishDraftLine();
      return;
    }
    if (
      event.key === "Enter" &&
      tool.value === "water" &&
      waterMode.value === "river" &&
      riverDraftHasPoints(riverDraft.value)
    ) {
      event.preventDefault();
      finishRiverDraft();
      return;
    }
    if (
      (event.key === "Delete" || event.key === "Backspace") &&
      isJust(selection.value)
    ) {
      event.preventDefault();
      deleteSelection();
    }
  }

  function onKeyUp(event: KeyboardEvent): void {
    if (event.code === "Space")
      void dispatch({ type: "SpaceHeldChanged", value: false });
  }

  function deleteSelection(): void {
    const selM = selection.value;
    if (isNothing(selM)) return;
    const sel = selM.value;
    eraseTarget(sel);
  }

  function eraseTarget(sel: Selection): void {
    if (sel.kind === "label") {
      if (canEditLabels.value === false) return;
      pushUndo();
      doc.value.labels = doc.value.labels.filter((l) => l.id !== sel.id);
      void dispatch({ type: "SelectionChanged", value: nothing });
      return;
    }
    if (sel.kind === "storyMarker") {
      pushUndo();
      doc.value.storyMarkers = doc.value.storyMarkers.filter(
        (m) => m.id !== sel.id,
      );
      void dispatch({ type: "SelectionChanged", value: nothing });
      scheduleDraw();
      return;
    }
    // Story path: geography layers are locked; only free markers delete above.
    if (storyPathLocksLayers.value === true) return;
    if (canEditLayer.value === false) return;
    const layerM = layerById(sel.layerId);
    if (isNothing(layerM)) return;
    const layer = layerM.value;
    pushUndo();
    if (sel.kind === "path") {
      layer.paths = layer.paths.filter((p) => p.id !== sel.pathId);
      pruneOrphanNodes(layer);
    } else if (sel.kind === "node") {
      for (const path of layer.paths) {
        path.nodeIds = path.nodeIds.filter((id) => id !== sel.nodeId);
      }
      layer.paths = layer.paths.filter((p) => p.nodeIds.length >= 2);
      pruneOrphanNodes(layer);
    } else if (sel.kind === "landmark") {
      layer.landmarks = layer.landmarks.filter((l) => l.id !== sel.id);
    } else if (sel.kind === "image") {
      layer.images = layer.images.filter((img) => img.id !== sel.id);
    } else if (sel.kind === "fill") {
      layer.fills = layer.fills.filter((f) => f.id !== sel.id);
    }
    void dispatch({ type: "SelectionChanged", value: nothing });
  }

  function splitSelectedNode(): void {
    const selected = selection.value;
    if (
      isNothing(selected) ||
      selected.value.kind !== "node" ||
      selected.value.pathId.length === 0
    )
      return;
    const sel = selected.value;
    const layerM = layerById(sel.layerId);
    if (isNothing(layerM)) return;
    const layer = layerM.value;
    const found = toMaybe(layer.paths.find((p) => p.id === sel.pathId));
    if (isNothing(found)) return;
    const path = found.value;
    const idx = path.nodeIds.indexOf(sel.nodeId);
    if (idx <= 0 || idx >= path.nodeIds.length - 1) return;
    pushUndo();
    const tail = path.nodeIds.slice(idx);
    path.nodeIds = path.nodeIds.slice(0, idx + 1);
    layer.paths.push({
      ...path,
      id: newMapId(),
      nodeIds: tail,
      color: path.color,
      weight: path.weight,
    });
  }

  const labelPreview = computed<Maybe<MapLabel>>(() => {
    if (labelDialogOpen.value === false) return nothing;
    const existing = andThenMaybe(editingLabel.value, (editing) =>
      labelById(editing.id),
    );
    const origin = isJust(existing) ? existing : labelPlacement.value;
    if (isNothing(origin)) return nothing;
    return just({
      id: "label-preview",
      x: origin.value.x,
      y: origin.value.y,
      text: labelDraft.value.length > 0 ? labelDraft.value : "Label",
      size: labelSize.value,
      font: labelFont.value,
      rotation: labelRotation.value,
      curve: labelCurve.value,
      letterSpacing: labelSpacing.value,
      color: inkColor.value,
    });
  });

  const labelEditorStyle = computed<CSSProperties>(() => {
    const existing = andThenMaybe(editingLabel.value, (editing) =>
      labelById(editing.id),
    );
    const origin = isJust(existing) ? existing : labelPlacement.value;
    if (isNothing(origin)) return { display: "none" };
    const point = toScreen(origin.value);
    const fontPx = labelFontPx(
      { size: labelSize.value },
      doc.value.viewport.zoom,
    );
    const draft = {
      id: "draft",
      x: 0,
      y: 0,
      text: labelDraft.value.length > 0 ? labelDraft.value : "Label",
      font: labelFont.value,
    };
    if (labelCurve.value !== 0 && isJust(labelPreview.value)) {
      const box = labelSelectionBox(labelPreview.value.value);
      point.y =
        box.y + box.h + fontPx * draft.text.split("\n").length * 0.6 + 20;
    }
    return {
      left: `${point.x}px`,
      top: `${point.y}px`,
      width: `${Math.max(80, measureLabelWidth(draft, fontPx) + Math.max(...draft.text.split("\n").map((line) => Math.max(0, Array.from(line).length - 1))) * labelSpacing.value * doc.value.viewport.zoom + 20)}px`,
      height: `${fontPx * 1.2 * draft.text.split("\n").length + 8}px`,
      font: labelCanvasFont(fontPx, labelFont.value),
      transform: `translate(-50%, -50%) rotate(${labelCurve.value === 0 ? labelRotation.value : 0}deg)`,
      lineHeight: "1.2",
      letterSpacing: `${labelSpacing.value * doc.value.viewport.zoom}px`,
      color: inkColor.value,
    };
  });

  function beginEditLabel(id: string): void {
    if (canEditLabels.value === false) return;
    const labelM = labelById(id);
    if (isNothing(labelM)) return;
    const label = labelM.value;
    void dispatch({ type: "InkColorChanged", value: labelInk(label) });
    void dispatch({
      type: "LabelFontChanged",
      value: label.font ?? "cartographic",
    });
    void dispatch({ type: "LabelRotationChanged", value: label.rotation ?? 0 });
    void dispatch({ type: "LabelCurveChanged", value: label.curve ?? 0 });
    void dispatch({
      type: "LabelSpacingChanged",
      value: label.letterSpacing ?? 0,
    });
    void dispatch({
      type: "LabelEditStarted",
      id,
      text: label.text,
      size: labelScale(label),
    });
  }

  function cancelLabelDialog(): void {
    const label = andThenMaybe(editingLabel.value, (editing) =>
      labelById(editing.id),
    );
    if (isJust(label)) {
      void dispatch({ type: "InkColorChanged", value: labelInk(label.value) });
      void dispatch({
        type: "LabelFontChanged",
        value: label.value.font ?? "cartographic",
      });
      void dispatch({
        type: "LabelCurveChanged",
        value: label.value.curve ?? 0,
      });
      void dispatch({
        type: "LabelSpacingChanged",
        value: label.value.letterSpacing ?? 0,
      });
      void dispatch({
        type: "LabelRotationChanged",
        value: label.value.rotation ?? 0,
      });
    }
    void dispatch({
      type: "LabelDialogClosed",
      size: isJust(label) ? labelScale(label.value) : labelSize.value,
    });
  }

  function storedScale(scale: number): Maybe<number> {
    const next = clampLabelSize(scale);
    return next === 1 ? nothing : just(next);
  }

  function commitLabel(): void {
    if (canEditLabels.value === false) {
      cancelLabelDialog();
      return;
    }
    const text = labelDraft.value.trim();
    const nextSize = storedScale(labelSize.value);
    const nextInk = storedMapInk(inkColor.value);
    if (isJust(editingLabel.value)) {
      const { id } = editingLabel.value.value;
      const labelM = labelById(id);
      if (isJust(labelM)) {
        const label = labelM.value;
        const nextText = text.length > 0 ? text : label.text;
        const textChanged = nextText !== label.text;
        const sizeChanged =
          (label.size ?? 1) !== (isJust(nextSize) ? nextSize.value : 1);
        if (
          textChanged ||
          sizeChanged ||
          labelInk(label) !== inkColor.value ||
          (label.font ?? "cartographic") !== labelFont.value ||
          (label.rotation ?? 0) !== labelRotation.value ||
          (label.curve ?? 0) !== labelCurve.value ||
          (label.letterSpacing ?? 0) !== labelSpacing.value
        ) {
          pushUndo();
          label.text = nextText;
          label.font = labelFont.value;
          label.rotation = labelRotation.value;
          label.curve = labelCurve.value;
          label.letterSpacing = labelSpacing.value;
          if (isJust(nextInk)) label.color = nextInk.value;
          else delete label.color;
          if (isNothing(nextSize)) delete label.size;
          else label.size = nextSize.value;
        }
      }
      cancelLabelDialog();
      if (isJust(labelM))
        void dispatch({
          type: "SelectionChanged",
          value: just({ kind: "label", id }),
        });
      return;
    }
    const at = labelPlacement.value;
    if (text.length === 0 || isNothing(at)) {
      cancelLabelDialog();
      return;
    }
    pushUndo();
    const ink = storedMapInk(inkColor.value);
    const label: MapLabel = {
      id: newMapId(),
      x: at.value.x,
      y: at.value.y,
      text,
      font: labelFont.value,
      rotation: labelRotation.value,
      curve: labelCurve.value,
      letterSpacing: labelSpacing.value,
      ...(isJust(nextSize) ? { size: nextSize.value } : {}),
      ...(isJust(ink) ? { color: ink.value } : {}),
    };
    doc.value.labels.push(label);
    void dispatch({
      type: "SelectionChanged",
      value: just({ kind: "label", id: label.id }),
    });
    cancelLabelDialog();
  }

  function onDblClick(event: MouseEvent): void {
    if (readonly.value) return;
    const screenM = pointerScreen(event);
    if (isNothing(screenM)) return;
    const screen = screenM.value;
    const world = toWorld(screen);
    const picked = pickAt(world, screen);
    if (isNothing(picked)) return;
    const hit = picked.value;
    if (hit.kind === "label") {
      beginEditLabel(hit.id);
      return;
    }
    if (hit.kind === "path") {
      const layerM = layerById(hit.layerId);
      if (isNothing(layerM)) return;
      const layer = layerM.value;
      const found = toMaybe(layer.paths.find((p) => p.id === hit.pathId));
      if (isNothing(found)) return;
      const path = found.value;
      let bestIdx = 0;
      let bestDist = Infinity;
      const pts = pathPoints(layer, path);
      for (let i = 0; i < pts.length - 1; i++) {
        const a = atIndexMaybe(pts, i);
        const b = atIndexMaybe(pts, i + 1);
        if (isNothing(a) || isNothing(b)) continue;
        const d = distanceToSegment(world, a.value, b.value);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      if (bestDist > joinThresholdWorld(doc.value.viewport.zoom) * 1.5) return;
      pushUndo();
      const node = { id: newMapId(), x: world.x, y: world.y };
      layer.nodes.push(node);
      path.nodeIds.splice(bestIdx + 1, 0, node.id);
      void dispatch({
        type: "SelectionChanged",
        value: just({
          kind: "node",
          layerId: hit.layerId,
          nodeId: node.id,
          pathId: path.id,
        }),
      });
    }
  }

  function cleanup(): void {
    if (isJust(raf)) cancelAnimationFrame(raf.value);
    if (isJust(zoomRaf)) cancelAnimationFrame(zoomRaf.value);
    zoomRaf = nothing;
    zoomTarget = nothing;
  }

  return {
    tool,
    selection,
    inkColor,
    fillColor,
    inkWeight,
    inkStyle,
    panning,
    spaceHeld,
    landmarkStamp,
    imageStamp,
    labelSize,
    labelFont,
    labelRotation,
    labelCurve,
    labelSpacing,
    applyLabelAppearance,
    labelDraft,
    labelDialogOpen,
    labelEditorStyle,
    editingLabel,
    shortcutsOpen,
    inkTargetSelected,
    fillTargetSelected,
    sizeSliderKind,
    objectSizeMin,
    showResizeCursor,
    resizeCursor,
    sizeSliderModel,
    landmassBrushSize,
    landmassCoastRoughness,
    terrainBrushStyle,
    terrainOpacity,
    terrainCoverage,
    waterMode,
    undoDepth,
    redoDepth,
    pushUndo,
    undo,
    redo,
    selectedLandmark,
    selectedImage,
    selectLandmarkById,
    getLoadedMapImage,
    storyPathHover,
    storyPathSelection,
    deleteSelection,
    cancelDraftLine,
    setTool,
    scheduleDraw,
    applyInkColor,
    applyFillColor,
    applyInkWeight,
    applyInkStyle,
    splitSelectedNode,
    toggleShortcuts,
    closeShortcuts,
    cancelLabelDialog,
    commitLabel,
    setLandmassFromImage,
    getLandmassCanvas,
    getLandmassCoasts,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave,
    onWheel,
    onDblClick,
    onContextMenu,
    onKeyDown,
    onKeyUp,
    layerById,
    cleanup,
  };
}
